import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiDocument,
  DocumentAuditEvent,
  DocumentLifecycleStatus,
  DocumentTask,
  DocumentTaskQueue,
  EvidenceManifestExportResult,
  FinalXmlArchiveRetrievalResult,
  SignatureVerificationResult,
  SupplementaryDocumentEvidence,
  exportEvidenceManifestFromAPI,
  getDocumentAuditEventsFromAPI,
  getDocumentFromAPI,
  getDocumentTaskQueueFromAPI,
  updateDocumentTaskStatusInAPI,
  getFinalXmlArchiveFromAPI,
  isAppendOnlyLifecyclePersistenceUnavailableError,
  isDocumentAuditAccessPersistenceUnavailableError,
  isDocumentAuditAccessStateChangedError,
  isSupplementaryDocumentIntegrityLinkExportError,
  isSupplementaryDocumentIntegrityLinkFinalXmlError,
  isDocumentLifecycleStateChangedError,
  listSupplementaryDocumentsInAPI,
  transitionDocumentInAPI,
  verifyDocumentSignaturesInAPI,
} from '../services/eCMRService';

interface EvidenceDashboardState {
  document?: ApiDocument;
  auditEvents: DocumentAuditEvent[];
  auditAccessIssue?: 'state_changed' | 'persistence_unavailable' | 'unavailable';
  attachments: SupplementaryDocumentEvidence[];
  signatureVerification?: SignatureVerificationResult;
  finalXmlArchive?: FinalXmlArchiveRetrievalResult;
  finalXmlArchiveIssue?: 'supplementary_integrity_required';
  evidenceManifest?: EvidenceManifestExportResult;
  evidenceManifestIssue?: 'supplementary_integrity_required';
  taskQueue?: DocumentTaskQueue;
}

type EvidenceResult<T> = PromiseSettledResult<T>;

const settledValue = <T,>(result: EvidenceResult<T>, fallback: T): T =>
  result.status === 'fulfilled' ? result.value : fallback;

const optionalSettledValue = <T,>(result: EvidenceResult<T>): T | undefined =>
  result.status === 'fulfilled' ? result.value : undefined;

const documentAuditAccessIssue = (
  result: EvidenceResult<DocumentAuditEvent[]>
): EvidenceDashboardState['auditAccessIssue'] => {
  if (result.status !== 'rejected') return undefined;
  if (isDocumentAuditAccessStateChangedError(result.reason)) return 'state_changed';
  if (isDocumentAuditAccessPersistenceUnavailableError(result.reason)) return 'persistence_unavailable';
  return 'unavailable';
};

const countLabel = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

const GENERIC_DASHBOARD_LOAD_ERROR = 'No se pudo cargar el panel de evidencias desde backend.';
const GENERIC_EXCEPTION_REVIEW_UPDATE_ERROR = 'No se pudo actualizar el estado de revisión.';
const GENERIC_LIFECYCLE_UPDATE_ERROR = 'No se pudo actualizar el ciclo de vida.';
const LIFECYCLE_CONFLICT_UPDATE_MESSAGE =
  'El estado del documento cambió en otra sesión. Se ha recargado el estado backend.';
const LIFECYCLE_CONFLICT_RELOAD_ERROR =
  'El estado del documento cambió en otra sesión y no se pudo recargar el estado backend.';
const LIFECYCLE_PERSISTENCE_UNCERTAIN_MESSAGE =
  'No se pudo confirmar la transición. Se ha recargado el estado backend; compruébalo antes de reintentar.';
const LIFECYCLE_PERSISTENCE_UNCERTAIN_RELOAD_ERROR =
  'No se pudo confirmar la transición ni recargar el estado backend. Recarga el documento antes de reintentar.';

interface AuditReviewCategory {
  key: string;
  label: string;
  nextActionLabel: string;
  eventTypes: Set<string>;
}

interface AuditReviewPriority extends Omit<AuditReviewCategory, 'eventTypes'> {
  count: number;
}

const auditIntegrityTamperEventTypes = new Set([
  'document.integrity.compromised',
]);

const auditValidationFailureEventTypes = new Set([
  'document.cmr_business_validation.failed',
  'document.ecmr_protocol_validation.failed',
  'document.validation.failed',
  'document.xml.validation.failed',
]);

const auditRejectedActionEventTypes = new Set([
  'document.amendment.conflict_rejected',
  'document.amendment_acknowledgement.conflict_rejected',
  'document.attachment.upload_rejected',
  'document.carriage_accounting.conflict_rejected',
  'document.carriage_impediment.conflict_rejected',
  'document.carrier_liability_incident.conflict_rejected',
  'document.carrier_reservation.conflict_rejected',
  'document.carrier_reservation_acknowledgement.conflict_rejected',
  'document.carrier_takeover_check.conflict_rejected',
  'document.carrier_takeover_check_request.conflict_rejected',
  'document.claim_reservation.conflict_rejected',
  'document.cod_delivery_omission.conflict_rejected',
  'document.compensation_assessment.conflict_rejected',
  'document.compensation_interest.conflict_rejected',
  'document.consignee_delivery_request.conflict_rejected',
  'document.consignment_note_copy_access.conflict_rejected',
  'document.dangerous_goods_notice.conflict_rejected',
  'document.delivery_confirmation.conflict_rejected',
  'document.delivery_reservation_acknowledgement.conflict_rejected',
  'document.delivery_timing_assessment.conflict_rejected',
  'document.disposal_execution_notice.conflict_rejected',
  'document.disposal_instruction.conflict_rejected',
  'document.dispute_procedure.conflict_rejected',
  'document.ecmr_receipt_access.conflict_rejected',
  'document.liability_limitation.conflict_rejected',
  'document.multimodal_carriage.conflict_rejected',
  'document.prohibited_clause.conflict_rejected',
  'document.replacement.conflict_rejected',
  'document.sender_particulars_responsibility.conflict_rejected',
  'document.signature.conflict_rejected',
  'document.successive_carrier.conflict_rejected',
]);

const auditDownloadFailureEventTypes = new Set([
  'document.evidence_download.failed',
]);

const mergeAuditEventTypes = (...eventTypeGroups: Array<Set<string>>): Set<string> => {
  const merged = new Set<string>();
  eventTypeGroups.forEach((eventTypes) => {
    eventTypes.forEach((eventType) => {
      merged.add(eventType);
    });
  });
  return merged;
};

const auditReviewCategories: AuditReviewCategory[] = [
  {
    key: 'integrity-tamper',
    label: 'Integridad/tamper',
    nextActionLabel: 'Verificar cadena de custodia y retención',
    eventTypes: auditIntegrityTamperEventTypes,
  },
  {
    key: 'validation-failure',
    label: 'Fallo de validación',
    nextActionLabel: 'Revisar reglas de validación y corregir evidencia',
    eventTypes: auditValidationFailureEventTypes,
  },
  {
    key: 'rejected-upload-action',
    label: 'Carga o acción rechazada',
    nextActionLabel: 'Coordinar corrección con la parte responsable',
    eventTypes: auditRejectedActionEventTypes,
  },
  {
    key: 'download-failure',
    label: 'Fallo de descarga',
    nextActionLabel: 'Reintentar descarga desde evidencia backend',
    eventTypes: auditDownloadFailureEventTypes,
  },
];

const auditExceptionReviewStatusUpdateEventTypes = new Set(['document.exception_review_task.status_updated']);

const auditExceptionReviewEventTypes = mergeAuditEventTypes(
  auditIntegrityTamperEventTypes,
  auditValidationFailureEventTypes,
  auditRejectedActionEventTypes,
  auditDownloadFailureEventTypes
);

const auditSignatureEventTypes = new Set([
  'document.signed',
  'document.signature.conflict_rejected',
  'document.signature.created',
  'document.signature.verification.failed',
]);

const auditProcedureEventTypes = new Set([
  'document.procedure.accepted',
  'document.procedure_set.accepted',
  'document.procedure_set.reconciled',
  'document.procedure_set.reviewed',
  'document.procedure_set.updated',
]);

const auditLifecycleTransitionAcceptedEventTypes = new Set([
  'document.accepted_by_carrier',
  'document.closed',
  'document.delivered',
  'document.in_transit',
  'document.issued',
  'document.lifecycle.transitioned',
]);

const auditLifecycleTransitionRejectedEventTypes = new Set([
  'document.lifecycle.transition_rejected',
]);

const auditLifecycleEventTypes = mergeAuditEventTypes(
  auditLifecycleTransitionAcceptedEventTypes,
  auditLifecycleTransitionRejectedEventTypes
);

const auditEvidenceManifestEventTypes = new Set([
  'document.evidence_manifest.archived',
  'document.evidence_manifest.exported',
]);

const auditDeliveryEventTypes = new Set([
  'document.consignee_delivery_request.conflict_rejected',
  'document.consignee_delivery_request.recorded',
  'document.delivery.confirmed',
  'document.delivery_confirmation.conflict_rejected',
  'document.delivery_reservation_acknowledgement.conflict_rejected',
  'document.delivery_reservation_acknowledgement.recorded',
  'document.delivery_timing_assessment.conflict_rejected',
  'document.delivery_timing_assessment.recorded',
  'document.proof_of_delivery.reviewed',
]);

const auditCarrierReservationEventTypes = new Set([
  'document.carrier_reservation.conflict_rejected',
  'document.carrier_reservation.created',
  'document.carrier_reservation.reviewed',
  'document.carrier_reservation_acknowledgement.conflict_rejected',
  'document.carrier_reservation_acknowledgement.recorded',
]);

const auditAttachmentEventTypes = new Set([
  'document.attachment.direct_upload.confirmed',
  'document.attachment.download_url.created',
  'document.attachment.upload_intent.created',
  'document.attachment.upload_rejected',
  'document.attachment.uploaded',
  'document.attachments.listed',
]);

const auditRetentionEventTypes = new Set([
  'document.legal_hold.activated',
  'document.legal_hold.applied',
  'document.legal_hold.released',
  'document.retention_policy.applied',
  'document.retention_policy.reviewed',
]);

const auditEventType = (event: DocumentAuditEvent): string =>
  typeof event.eventType === 'string' ? event.eventType : 'other';

const auditEventMatches = (event: DocumentAuditEvent, eventTypes: Set<string>): boolean =>
  eventTypes.has(auditEventType(event));

const auditSignalsByType = (events: DocumentAuditEvent[], eventTypes: Set<string>): DocumentAuditEvent[] =>
  events.filter((event) => auditEventMatches(event, eventTypes));

const auditReviewPriorities = (events: DocumentAuditEvent[]): AuditReviewPriority[] =>
  auditReviewCategories
    .map(({ eventTypes, ...category }) => ({
      ...category,
      count: auditSignalsByType(events, eventTypes).length,
    }))
    .filter((category) => category.count > 0);

interface ExceptionReviewSummary {
  criticalCount: number;
  operationalCount: number;
}

const exceptionReviewSummary = (events: DocumentAuditEvent[]): ExceptionReviewSummary => {
  return events.reduce(
    (summary, event) => {
      if (
        auditEventMatches(event, auditIntegrityTamperEventTypes) ||
        auditEventMatches(event, auditValidationFailureEventTypes)
      ) {
        summary.criticalCount += 1;
      } else if (
        auditEventMatches(event, auditRejectedActionEventTypes) ||
        auditEventMatches(event, auditDownloadFailureEventTypes)
      ) {
        summary.operationalCount += 1;
      }
      return summary;
    },
    { criticalCount: 0, operationalCount: 0 }
  );
};

const legacyTaskEvidenceGaps = new Set([
  'procedure_acceptance_missing',
  'procedure_acceptance_role_coverage_missing',
  'carrier_takeover_check_missing',
  'carrier_requested_check_result_missing',
  'carrier_reservation_absent',
  'carrier_reservation_acknowledgement_missing',
  'carrier_acceptance_missing',
  'transit_start_missing',
  'delivery_confirmation_missing',
  'delivery_reservation_acknowledgement_missing',
  'signature_evidence_missing',
  'signature_verification_review_missing',
  'contract_participant_identity_missing',
  'contract_signature_role_coverage_missing',
  'supplementary_documents_absent',
  'amendment_acknowledgement_pending',
  'retention_governance_missing',
  'final_xml_archive_missing',
  'evidence_manifest_archive_missing',
  'final_xml_download_completion_missing',
  'evidence_manifest_download_completion_missing',
  'supplementary_attachment_download_completion_missing',
  'exception_review_required',
]);

const safeLegacyEvidenceGap = (task: DocumentTask): string | undefined =>
  task.evidenceGap && legacyTaskEvidenceGaps.has(task.evidenceGap) ? task.evidenceGap : undefined;

const taskHasLegacyEvidenceGap = (task: DocumentTask, allowedGaps: Set<string>): boolean => {
  const evidenceGap = safeLegacyEvidenceGap(task);
  return Boolean(evidenceGap && allowedGaps.has(evidenceGap));
};

const exceptionReviewTaskIds = new Set(['exception_review_required']);
const exceptionReviewEvidenceGaps = new Set(['exception_review_required']);
const archiveReadinessTaskIds = new Set(['final_xml_archive_review', 'evidence_manifest_archive_review']);
const archiveReadinessEvidenceGaps = new Set(['final_xml_archive_missing', 'evidence_manifest_archive_missing']);
const signatureVerificationReviewTaskIds = new Set(['signature_verification_review']);
const signatureVerificationReviewEvidenceGaps = new Set(['signature_verification_review_missing']);

const isExceptionReviewTask = (task: DocumentTask): boolean =>
  exceptionReviewTaskIds.has(task.taskId) || taskHasLegacyEvidenceGap(task, exceptionReviewEvidenceGaps);

const isArchiveReadinessTask = (task: DocumentTask): boolean =>
  archiveReadinessTaskIds.has(task.taskId) || taskHasLegacyEvidenceGap(task, archiveReadinessEvidenceGaps);

const operationalAcknowledgementTaskIds = new Set([
  'carrier_takeover_check_required',
  'carrier_requested_check_result_required',
  'carrier_reservation_review',
  'carrier_reservation_acknowledgement_review',
  'delivery_reservation_acknowledgement_review',
  'amendment_acknowledgement_review',
]);

const isOperationalAcknowledgementTask = (task: DocumentTask): boolean =>
  operationalAcknowledgementTaskIds.has(task.taskId);

const isSignatureVerificationReviewTask = (task: DocumentTask): boolean =>
  signatureVerificationReviewTaskIds.has(task.taskId) ||
  taskHasLegacyEvidenceGap(task, signatureVerificationReviewEvidenceGaps);

const isDownloadCompletionReviewTask = (task: DocumentTask): boolean =>
  task.taskId === 'final_xml_download_completion_review' ||
  task.taskId === 'evidence_manifest_download_completion_review' ||
  task.taskId === 'supplementary_attachment_download_completion_review';

const isTransportLifecycleWorkflowTask = (task: DocumentTask): boolean =>
  task.taskId === 'carrier_acceptance_required' ||
  task.taskId === 'transit_start_required' ||
  task.taskId === 'delivery_confirmation_required';

const complianceGovernanceWorkflowTaskIds = new Set([
  'procedure_acceptance_required',
  'procedure_acceptance_role_coverage_required',
  'server_side_signature_required',
  'contract_signature_role_coverage_required',
  'retention_governance_review',
]);

const isComplianceGovernanceWorkflowTask = (task: DocumentTask): boolean =>
  complianceGovernanceWorkflowTaskIds.has(task.taskId);

const uniqueSorted = (values: Array<string | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort();

const publicOperationalRoles = new Set(['owner', 'consignor', 'carrier', 'consignee', 'authority', 'delegate']);

const publicOperationalRole = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return publicOperationalRoles.has(normalized) ? normalized : undefined;
};

const uniqueSortedOperationalRoles = (values: unknown[]): string[] =>
  Array.from(new Set(values.map(publicOperationalRole).filter((value): value is string => Boolean(value)))).sort();

const publicSignatureMethods = new Set([
  'simple-electronic-attestation',
  'manual_capture',
  'manual_attestation',
]);

const publicSignatureMethod = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'simple_attestation') {
    return 'simple-electronic-attestation';
  }
  if (normalized === 'manual-capture') {
    return 'manual_capture';
  }

  return publicSignatureMethods.has(normalized) ? normalized : undefined;
};

const uniqueSortedSignatureMethods = (values: unknown[]): string[] =>
  Array.from(new Set(values.map(publicSignatureMethod).filter((value): value is string => Boolean(value)))).sort();

const publicCarrierReservationCategories = new Set([
  'package_count',
  'marks_numbers',
  'condition',
  'packaging',
  'unable_to_verify',
  'other',
]);

const publicCarrierReservationCategory = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return publicCarrierReservationCategories.has(normalized) ? normalized : undefined;
};

const uniqueSortedCarrierReservationCategories = (values: unknown[]): string[] =>
  Array.from(new Set(values.map(publicCarrierReservationCategory).filter((value): value is string => Boolean(value)))).sort();

const safePublicVersionLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim();
  return /^[A-Za-z0-9._:-]{1,64}$/.test(normalized) ? normalized : 'other';
};

const uniqueSortedVersionLabels = (values: unknown[]): string[] =>
  Array.from(new Set(values.map(safePublicVersionLabel).filter((value): value is string => Boolean(value)))).sort();

const publicExceptionReviewerGroups = new Set(['compliance_review', 'legal_review', 'operations_review']);

const publicExceptionReviewerGroup = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  return publicExceptionReviewerGroups.has(normalized) ? normalized : undefined;
};

const uniqueSortedExceptionReviewerGroups = (values: unknown[]): string[] =>
  Array.from(new Set(values.map(publicExceptionReviewerGroup).filter((value): value is string => Boolean(value)))).sort();

type SourceRecordDocument = ApiDocument & Record<string, unknown>;
type EvidenceRecord = Record<string, any>;
type BackendVersionReferenceRecord = {
  documentVersionId?: string;
  documentVersionReferencePresent?: boolean;
  documentVersionNumber?: number;
};

const hasBackendVersionReference = (record: BackendVersionReferenceRecord | undefined): boolean =>
  Boolean(record && (
    record.documentVersionReferencePresent ||
    record.documentVersionId ||
    record.documentVersionNumber !== undefined
  ));

const backendVersionReferenceKey = (record: BackendVersionReferenceRecord): string | undefined => {
  if (record.documentVersionId) {
    return `id:${record.documentVersionId}`;
  }

  if (record.documentVersionNumber !== undefined) {
    return `number:${record.documentVersionNumber}`;
  }

  return record.documentVersionReferencePresent ? 'available' : undefined;
};

const procedureSetReferenceKey = (acceptance: EvidenceRecord, index: number): string | undefined => {
  if (acceptance.procedureSetId) {
    return `id:${acceptance.procedureSetId}`;
  }

  if (acceptance.procedureSetVersion) {
    return `version:${acceptance.procedureSetVersion}`;
  }

  return acceptance.procedureSetReferencePresent ? `present:${index}` : undefined;
};

const hasOwnRecordProperty = (document: ApiDocument | undefined, property: string): boolean =>
  Boolean(document && Object.prototype.hasOwnProperty.call(document, property));

const sourceRecordArray = <T,>(
  document: ApiDocument | undefined,
  sourceRecordProperty: string,
  legacyValue: T[] | undefined
): T[] => {
  if (hasOwnRecordProperty(document, sourceRecordProperty)) {
    const sourceRecordValue = (document as SourceRecordDocument)[sourceRecordProperty];
    return Array.isArray(sourceRecordValue) ? (sourceRecordValue as T[]) : [];
  }

  return legacyValue ?? [];
};

const sourceRecordSingleton = <T,>(
  document: ApiDocument | undefined,
  sourceRecordProperty: string,
  legacyValue: T | undefined
): T | undefined => {
  if (hasOwnRecordProperty(document, sourceRecordProperty)) {
    const sourceRecords = (document as SourceRecordDocument)[sourceRecordProperty];
    return Array.isArray(sourceRecords) ? (sourceRecords[0] as T | undefined) : undefined;
  }

  return legacyValue;
};

const serverTaskMetadataSummary = (task: DocumentTask): string => {
  const metadata: string[] = [];
  const roleCount = uniqueSortedOperationalRoles(task.roles ?? []).length;

  if (roleCount > 0) {
    metadata.push(countLabel(roleCount, 'rol asignado', 'roles asignados'));
  }

  if (task.evidenceGapPresent === true || safeLegacyEvidenceGap(task)) {
    metadata.push('1 brecha de evidencia');
  }

  if (hasBackendVersionReference(task)) {
    metadata.push('referencia de versión backend');
  }

  return metadata.length > 0 ? `metadatos backend sanitizados: ${metadata.join(' · ')}` : '';
};

const statusClass = (available: boolean): string =>
  available
    ? 'border-green-500 bg-green-50 text-green-900'
    : 'border-yellow-500 bg-yellow-50 text-yellow-900';

const EvidenceCard: React.FC<{
  title: string;
  status: string;
  available: boolean;
  children?: React.ReactNode;
}> = ({ title, status, available, children }) => (
  <section className={`rounded border-l-4 p-4 shadow-sm ${statusClass(available)}`}>
    <h2 className="text-lg font-semibold">{title}</h2>
    <p className="mt-1 font-medium">{status}</p>
    {children && <div className="mt-2 text-sm">{children}</div>}
  </section>
);

interface PendingTask {
  label: string;
  href: string;
  actionLabel: string;
}

interface DashboardTask extends PendingTask {
  key: string;
  sourceTask?: DocumentTask;
}

type SafeServerTaskStatus = 'open' | 'in_progress' | 'completed' | 'deferred';

const serverTaskStatusLabels: Record<SafeServerTaskStatus, string> = {
  open: 'abierta',
  in_progress: 'en revision',
  completed: 'completada',
  deferred: 'diferida',
};

const safeServerTaskStatusLabel = (status: string): string =>
  serverTaskStatusLabels[status as SafeServerTaskStatus] ?? 'estado no reconocido omitido';

const serverTaskPresentationById = (taskId: string, documentId: string): PendingTask | undefined => {
  switch (taskId) {
    case 'contract_participant_identity_required':
      return {
        label: 'Identidades de remitente y transportista pendientes',
        href: `/workflow/${documentId}`,
        actionLabel: 'Vincular identidades',
      };
    case 'entitled_party_access_required':
      return {
        label: 'Acceso de participantes eCMR pendiente',
        href: `/workflow/${documentId}`,
        actionLabel: 'Restablecer acceso',
      };
    case 'manual_governance_review_required':
      return {
        label: 'Revision de gobernanza backend pendiente',
        href: `/workflow/${documentId}`,
        actionLabel: 'Abrir flujo seguro',
      };
    case 'manual_governance_completed':
      return {
        label: 'Gobernanza backend completada',
        href: `/workflow/${documentId}`,
        actionLabel: 'Abrir flujo seguro',
      };
    case 'retention_governance_required':
      return {
        label: 'Revision de retencion backend pendiente',
        href: `/retention/${documentId}`,
        actionLabel: 'Abrir retencion autorizada',
      };
    case 'supplementary_documents_review':
      return {
        label: 'Revision de documentos suplementarios pendiente',
        href: `/attachments/${documentId}`,
        actionLabel: 'Abrir adjuntos suplementarios',
      };
    case 'cmr_article5_copy_coverage_required':
      return {
        label: 'Cobertura de copias eCMR de la version actual pendiente',
        href: `/evidence/${documentId}`,
        actionLabel: 'Registrar copias y acceso',
      };
    case 'ecmr_article6_receipt_access_handover_required':
      return {
        label: 'Entrega eCMR Article 6.1 solicitada por el remitente pendiente',
        href: `/evidence/${documentId}`,
        actionLabel: 'Registrar entrega de recibo y acceso',
      };
    case 'ecmr_article6_supplementary_document_transfer_required':
      return {
        label: 'Entrega eCMR Article 6.2 de documentos suplementarios pendiente',
        href: `/attachments/${documentId}`,
        actionLabel: 'Gestionar entrega de adjuntos',
      };
    default:
      return undefined;
  }
};

const serverTaskPresentation = (task: DocumentTask, documentId: string, index: number): DashboardTask => {
  const knownPresentation = serverTaskPresentationById(task.taskId, documentId);
  const fallbackPresentation: PendingTask =
    task.status === 'completed'
      ? {
          label: 'Tarea backend completada por politica',
          href: `/workflow/${documentId}`,
          actionLabel: 'Abrir flujo seguro',
        }
      : {
          label: 'Tarea backend no clasificada pendiente',
          href: `/workflow/${documentId}`,
          actionLabel: 'Abrir flujo seguro',
        };

  return {
    ...(knownPresentation ?? fallbackPresentation),
    key: `server-task-${index}`,
    sourceTask: task,
  };
};

const EvidenceDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [state, setState] = useState<EvidenceDashboardState>({ auditEvents: [], attachments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exceptionReviewStatus, setExceptionReviewStatus] = useState<'open' | 'in_progress' | 'completed'>('in_progress');
  const [exceptionReviewGroup, setExceptionReviewGroup] = useState('compliance_review');
  const [exceptionReviewUpdateMessage, setExceptionReviewUpdateMessage] = useState('');
  const [exceptionReviewUpdating, setExceptionReviewUpdating] = useState(false);
  const [lifecycleNextStatus, setLifecycleNextStatus] = useState<DocumentLifecycleStatus>('accepted_by_carrier');
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleUpdateMessage, setLifecycleUpdateMessage] = useState('');
  const [lifecycleUpdating, setLifecycleUpdating] = useState(false);
  const [lifecycleReloadRequired, setLifecycleReloadRequired] = useState(false);

  useEffect(() => {
    if (!documentId) {
      setError('Missing API document id.');
      setLoading(false);
      return;
    }

    const loadDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const [
          documentResult,
          auditEventsResult,
          attachmentsResult,
          signatureVerificationResult,
          finalXmlArchiveResult,
          evidenceManifestResult,
          taskQueueResult,
        ] = await Promise.allSettled([
          getDocumentFromAPI(documentId),
          getDocumentAuditEventsFromAPI(documentId),
          listSupplementaryDocumentsInAPI(documentId),
          verifyDocumentSignaturesInAPI(documentId),
          getFinalXmlArchiveFromAPI(documentId),
          exportEvidenceManifestFromAPI(documentId),
          getDocumentTaskQueueFromAPI(documentId),
        ]);

        if (documentResult.status === 'rejected') {
          throw documentResult.reason;
        }

        setState({
          document: documentResult.value,
          auditEvents: settledValue(auditEventsResult, []),
          auditAccessIssue: documentAuditAccessIssue(auditEventsResult),
          attachments: settledValue(attachmentsResult, []),
          signatureVerification: optionalSettledValue(signatureVerificationResult),
          finalXmlArchive: optionalSettledValue(finalXmlArchiveResult),
          finalXmlArchiveIssue:
            finalXmlArchiveResult.status === 'rejected' &&
            isSupplementaryDocumentIntegrityLinkFinalXmlError(finalXmlArchiveResult.reason)
              ? 'supplementary_integrity_required'
              : undefined,
          evidenceManifest: optionalSettledValue(evidenceManifestResult),
          evidenceManifestIssue:
            evidenceManifestResult.status === 'rejected' &&
            isSupplementaryDocumentIntegrityLinkExportError(evidenceManifestResult.reason)
              ? 'supplementary_integrity_required'
              : undefined,
          taskQueue: optionalSettledValue(taskQueueResult),
        });
      } catch (loadError) {
        setError(GENERIC_DASHBOARD_LOAD_ERROR);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [documentId]);

  const verifiedSignatureCount = state.signatureVerification?.count ?? state.signatureVerification?.signatures?.length ?? 0;
  const signatureEvidence = sourceRecordArray<EvidenceRecord>(
    state.document,
    'signatureEvidenceRecords',
    (state.document?.signatureEvidence ?? state.signatureVerification?.signatures) as EvidenceRecord[] | undefined
  );
  const signatureEvidenceCount = signatureEvidence.length || verifiedSignatureCount;
  const signatureMethods = uniqueSortedSignatureMethods(signatureEvidence.map((signature) => signature.signatureMethod));
  const versionedSignatureEvidenceCount = uniqueSorted(signatureEvidence.map(backendVersionReferenceKey)).length;
  const signatureAuditSignals = auditSignalsByType(state.auditEvents, auditSignatureEventTypes);
  const versionedSignatureAuditSignalCount = signatureAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedSignatureAuditSignalCount = signatureAuditSignals.length - versionedSignatureAuditSignalCount;
  const hasSignatureAdministrationEvidence = signatureEvidenceCount > 0 || signatureAuditSignals.length > 0;
  const procedureAcceptances = sourceRecordArray<EvidenceRecord>(
    state.document,
    'procedureAcceptanceRecords',
    state.document?.procedureAcceptances as EvidenceRecord[] | undefined
  );
  const procedureAcceptanceCount = procedureAcceptances.length;
  const procedureSetReferenceCount = uniqueSorted(procedureAcceptances.map(procedureSetReferenceKey)).length;
  const procedureSetVersions = uniqueSortedVersionLabels(
    procedureAcceptances.map((acceptance) => acceptance.procedureSetVersion)
  );
  const procedureRoles = uniqueSortedOperationalRoles(
    procedureAcceptances.flatMap((acceptance) =>
      Array.isArray(acceptance.acceptedByRoles) ? acceptance.acceptedByRoles : []
    )
  );
  const currentVersion = state.document?.currentVersion;
  const finalXmlSupplementaryIntegrityRequired =
    state.finalXmlArchiveIssue === 'supplementary_integrity_required';
  const procedureBackendVersionReferenceCount = currentVersion ? 1 : 0;
  const procedureAuditSignals = auditSignalsByType(state.auditEvents, auditProcedureEventTypes);
  const procedureAuditSignalCount = procedureAuditSignals.length;
  const versionedProcedureAuditSignalCount = procedureAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedProcedureAuditSignalCount = procedureAuditSignalCount - versionedProcedureAuditSignalCount;
  const versionedProcedureAcceptanceCount = procedureAcceptances.filter((acceptance) => acceptance.procedureSetVersion).length;
  const procedureReconciliationReferenceCount = currentVersion && procedureAcceptanceCount > 0 ? 1 : 0;
  const procedureHandoffReady = procedureAcceptanceCount > 0 && procedureAuditSignalCount > 0;
  const procedureAdministrationPackageReady = procedureAcceptanceCount > 0 && procedureAuditSignalCount > 0;
  const lifecycleAuditSignals = auditSignalsByType(state.auditEvents, auditLifecycleEventTypes);
  const lifecycleVersionReferenceCount = lifecycleAuditSignals.filter(hasBackendVersionReference).length;
  const hasLifecycleAdministrationEvidence = lifecycleAuditSignals.length > 0;
  const lifecycleAcceptedTransitionCount = lifecycleAuditSignals.filter((event) =>
    auditEventMatches(event, auditLifecycleTransitionAcceptedEventTypes)
  ).length;
  const lifecycleAuthorizedReviewCount = lifecycleAuditSignals.filter((event) =>
    auditEventMatches(event, auditLifecycleTransitionRejectedEventTypes)
  ).length;
  const lifecyclePendingVersionReconciliationCount = lifecycleAuditSignals.length - lifecycleVersionReferenceCount;
  const manifestArchived = Boolean(state.evidenceManifest?.archiveReference);
  const evidenceManifestSupplementaryIntegrityRequired =
    state.evidenceManifestIssue === 'supplementary_integrity_required';
  const evidenceManifestAuditSignals = auditSignalsByType(state.auditEvents, auditEvidenceManifestEventTypes);
  const versionedEvidenceManifestAuditSignalCount = evidenceManifestAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedEvidenceManifestAuditSignalCount =
    evidenceManifestAuditSignals.length - versionedEvidenceManifestAuditSignalCount;
  const evidenceManifestVersionReferenceCount = hasBackendVersionReference(state.evidenceManifest?.archiveReference ?? undefined) ? 1 : 0;
  const hasEvidenceManifestAdministrationEvidence = manifestArchived || evidenceManifestAuditSignals.length > 0;
  const deliveryConfirmation = sourceRecordSingleton<EvidenceRecord>(
    state.document,
    'deliveryConfirmationRecords',
    state.document?.deliveryConfirmation as EvidenceRecord | undefined
  );
  const deliveryReservationCount = deliveryConfirmation?.deliveryReservations?.length ?? 0;
  const deliverySignatureCount = deliveryConfirmation?.signatureEvidence ? 1 : 0;
  const deliveryVersionReferenceCount = hasBackendVersionReference(deliveryConfirmation) ? 1 : 0;
  const deliveryAuditSignals = auditSignalsByType(state.auditEvents, auditDeliveryEventTypes);
  const versionedDeliveryAuditSignalCount = deliveryAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedDeliveryAuditSignalCount = deliveryAuditSignals.length - versionedDeliveryAuditSignalCount;
  const hasDeliveryAdministrationEvidence = Boolean(deliveryConfirmation) || deliveryAuditSignals.length > 0;
  const carrierReservations = sourceRecordArray<EvidenceRecord>(
    state.document,
    'carrierReservationRecords',
    state.document?.carrierReservations as EvidenceRecord[] | undefined
  );
  const carrierReservationCategories = uniqueSortedCarrierReservationCategories(
    carrierReservations.map((reservation) => reservation.category)
  );
  const versionedCarrierReservationCount = carrierReservations.filter(hasBackendVersionReference).length;
  const carrierReservationAuditSignals = auditSignalsByType(state.auditEvents, auditCarrierReservationEventTypes);
  const versionedCarrierReservationAuditSignalCount = carrierReservationAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedCarrierReservationAuditSignalCount =
    carrierReservationAuditSignals.length - versionedCarrierReservationAuditSignalCount;
  const hasCarrierReservationAdministrationEvidence = carrierReservations.length > 0 || carrierReservationAuditSignals.length > 0;
  const attachmentAuditSignals = auditSignalsByType(state.auditEvents, auditAttachmentEventTypes);
  const versionedAttachmentAuditSignalCount = attachmentAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedAttachmentAuditSignalCount = attachmentAuditSignals.length - versionedAttachmentAuditSignalCount;
  const externallyStoredAttachmentCount = state.attachments.filter((attachment) => attachment.payloadStoredExternally).length;
  const digestAvailableAttachmentCount = state.attachments.filter((attachment) => attachment.digestAvailable).length;
  const hasAttachmentAdministrationEvidence = state.attachments.length > 0 || attachmentAuditSignals.length > 0;
  const retentionPolicyRecords = sourceRecordArray<EvidenceRecord>(
    state.document,
    'retentionPolicyRecords',
    state.document?.retentionPolicy ? [state.document.retentionPolicy as EvidenceRecord] : []
  );
  const legalHoldRecords = sourceRecordArray<EvidenceRecord>(
    state.document,
    'legalHoldRecords',
    state.document?.legalHold ? [state.document.legalHold as EvidenceRecord] : []
  );
  const retentionPolicy = retentionPolicyRecords[0];
  const legalHold = legalHoldRecords[0];
  const retentionPolicyCount = retentionPolicyRecords.length;
  const activeLegalHoldCount = legalHoldRecords.filter((hold) => hold.active).length;
  const hasRetentionGovernance = retentionPolicyCount > 0 || activeLegalHoldCount > 0;
  const retentionAuditSignals = auditSignalsByType(state.auditEvents, auditRetentionEventTypes);
  const retentionAuditSignalCount = retentionAuditSignals.length;
  const versionedRetentionAuditSignalCount = retentionAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedRetentionAuditSignalCount = retentionAuditSignalCount - versionedRetentionAuditSignalCount;
  const hasRetentionAdministrationEvidence = hasRetentionGovernance && retentionAuditSignalCount > 0;
  const allServerTasks = state.taskQueue?.tasks ?? [];
  const exceptionReviewTasks = allServerTasks.filter(isExceptionReviewTask);
  const archiveReadinessTasks = allServerTasks.filter(isArchiveReadinessTask);
  const operationalAcknowledgementTasks = allServerTasks.filter(isOperationalAcknowledgementTask);
  const signatureVerificationReviewTasks = allServerTasks.filter(isSignatureVerificationReviewTask);
  const downloadCompletionReviewTasks = allServerTasks.filter(isDownloadCompletionReviewTask);
  const transportLifecycleWorkflowTasks = allServerTasks.filter(isTransportLifecycleWorkflowTask);
  const complianceGovernanceWorkflowTasks = allServerTasks.filter(isComplianceGovernanceWorkflowTask);
  const activeComplianceGovernanceWorkflowTasks = complianceGovernanceWorkflowTasks.filter((task) => task.status !== 'completed');
  const procedureGovernanceWorkflowCount = activeComplianceGovernanceWorkflowTasks.filter((task) =>
    task.taskId === 'procedure_acceptance_required' ||
    task.taskId === 'procedure_acceptance_role_coverage_required'
  ).length;
  const signatureGovernanceWorkflowCount = activeComplianceGovernanceWorkflowTasks.filter((task) =>
    task.taskId === 'server_side_signature_required' ||
    task.taskId === 'contract_signature_role_coverage_required'
  ).length;
  const retentionGovernanceWorkflowCount = activeComplianceGovernanceWorkflowTasks.filter((task) =>
    task.taskId === 'retention_governance_review'
  ).length;
  const complianceGovernanceBackendReferenceCount =
    activeComplianceGovernanceWorkflowTasks.filter(hasBackendVersionReference).length;
  const complianceGovernanceReferenceStatus =
    complianceGovernanceBackendReferenceCount > 0
      ? countLabel(
          complianceGovernanceBackendReferenceCount,
          'referencia backend disponible sin exponer identificadores crudos',
          'referencias backend disponibles sin exponer identificadores crudos'
        )
      : 'sin referencia backend versionada disponible';
  const complianceGovernanceWorkflowHref = `/workflow/${documentId}`;
  const complianceGovernanceRetentionHref = `/retention/${documentId}`;
  const activeTransportLifecycleWorkflowTasks = transportLifecycleWorkflowTasks.filter((task) => task.status !== 'completed');
  const carrierAcceptanceWorkflowCount = activeTransportLifecycleWorkflowTasks.filter((task) =>
    task.taskId === 'carrier_acceptance_required'
  ).length;
  const transitStartWorkflowCount = activeTransportLifecycleWorkflowTasks.filter((task) =>
    task.taskId === 'transit_start_required'
  ).length;
  const deliveryConfirmationWorkflowCount = activeTransportLifecycleWorkflowTasks.filter((task) =>
    task.taskId === 'delivery_confirmation_required'
  ).length;
  const transportLifecycleHasBackendVersionReference =
    activeTransportLifecycleWorkflowTasks.some(hasBackendVersionReference);
  const transportLifecycleHref = `/workflow/${documentId}`;
  const activeSignatureVerificationReviewTasks = signatureVerificationReviewTasks.filter((task) => task.status !== 'completed');
  const signatureVerificationReviewPolicyCount = activeSignatureVerificationReviewTasks.filter((task) =>
    task.taskId === 'signature_verification_review'
  ).length;
  const signatureVerificationReviewHasBackendVersionReference =
    activeSignatureVerificationReviewTasks.some(hasBackendVersionReference);
  const signatureVerificationReviewHref = `/workflow/${documentId}`;
  const activeDownloadCompletionReviewTasks = downloadCompletionReviewTasks.filter((task) => task.status !== 'completed');
  const finalXmlDownloadCompletionReviewCount = activeDownloadCompletionReviewTasks.filter((task) =>
    task.taskId === 'final_xml_download_completion_review'
  ).length;
  const evidenceManifestDownloadCompletionReviewCount = activeDownloadCompletionReviewTasks.filter((task) =>
    task.taskId === 'evidence_manifest_download_completion_review'
  ).length;
  const supplementaryAttachmentDownloadCompletionReviewCount = activeDownloadCompletionReviewTasks.filter((task) =>
    task.taskId === 'supplementary_attachment_download_completion_review'
  ).length;
  const downloadCompletionReviewBackendReferenceCount =
    activeDownloadCompletionReviewTasks.filter(hasBackendVersionReference).length;
  const downloadCompletionReviewReferenceStatus =
    downloadCompletionReviewBackendReferenceCount > 0
      ? countLabel(
          downloadCompletionReviewBackendReferenceCount,
          'referencia backend disponible sin exponer identificadores crudos',
          'referencias backend disponibles sin exponer identificadores crudos'
        )
      : 'sin referencia backend versionada disponible';
  const downloadCompletionReviewExportStatus =
    state.finalXmlArchive?.archiveReference && state.evidenceManifest?.archiveReference
      ? 'XML final y manifiesto disponibles para exportación segura'
      : 'exportación segura pendiente de referencias backend';
  const downloadCompletionReviewHref = `/export/${documentId}?source=api`;
  const activeOperationalAcknowledgementTasks = operationalAcknowledgementTasks.filter((task) => task.status !== 'completed');
  const carrierTakeoverCheckCount = activeOperationalAcknowledgementTasks.filter((task) =>
    task.taskId === 'carrier_takeover_check_required'
  ).length;
  const carrierRequestedCheckResultCount = activeOperationalAcknowledgementTasks.filter((task) =>
    task.taskId === 'carrier_requested_check_result_required'
  ).length;
  const carrierReservationReviewCount = activeOperationalAcknowledgementTasks.filter((task) =>
    task.taskId === 'carrier_reservation_review'
  ).length;
  const carrierReservationAcknowledgementCount = activeOperationalAcknowledgementTasks.filter((task) =>
    task.taskId === 'carrier_reservation_acknowledgement_review'
  ).length;
  const deliveryReservationAcknowledgementCount = activeOperationalAcknowledgementTasks.filter((task) =>
    task.taskId === 'delivery_reservation_acknowledgement_review'
  ).length;
  const amendmentAcknowledgementCount = activeOperationalAcknowledgementTasks.filter((task) =>
    task.taskId === 'amendment_acknowledgement_review'
  ).length;
  const operationalAcknowledgementHasBackendVersionReference =
    activeOperationalAcknowledgementTasks.some(hasBackendVersionReference);
  const operationalAcknowledgementHref = `/evidence/${documentId}`;
  const activeArchiveReadinessTasks = archiveReadinessTasks.filter((task) => task.status !== 'completed');
  const finalXmlArchiveReadinessCount = activeArchiveReadinessTasks.filter((task) =>
    task.taskId === 'final_xml_archive_review' || safeLegacyEvidenceGap(task) === 'final_xml_archive_missing'
  ).length;
  const evidenceManifestArchiveReadinessCount = activeArchiveReadinessTasks.filter((task) =>
    task.taskId === 'evidence_manifest_archive_review' || safeLegacyEvidenceGap(task) === 'evidence_manifest_archive_missing'
  ).length;
  const archiveReadinessHasBackendVersionReference =
    activeArchiveReadinessTasks.some(hasBackendVersionReference);
  const archiveReadinessHref = `/export/${documentId}`;
  const primaryExceptionReviewTask = exceptionReviewTasks[0];
  const regularServerTasks = allServerTasks.filter(
    (task) =>
      !isExceptionReviewTask(task) &&
      !isArchiveReadinessTask(task) &&
      !isOperationalAcknowledgementTask(task) &&
      !isSignatureVerificationReviewTask(task) &&
      !isDownloadCompletionReviewTask(task) &&
      !isTransportLifecycleWorkflowTask(task) &&
      !isComplianceGovernanceWorkflowTask(task)
  );
  const serverTasks = regularServerTasks.filter((task) => task.status !== 'completed');
  const completedServerTasks = regularServerTasks.filter((task) => task.status === 'completed');
  const auditReviewWorkbench = auditReviewPriorities(state.auditEvents);
  const auditReviewPriorityCount = auditReviewWorkbench.reduce((total, category) => total + category.count, 0);
  const auditedExceptionCategoryCount = auditReviewWorkbench.length;
  const exceptionSummary = exceptionReviewSummary(state.auditEvents);
  const hasExceptionReview = exceptionSummary.criticalCount > 0 || exceptionSummary.operationalCount > 0;
  const hasExceptionCoverageSummary = hasExceptionReview && exceptionReviewTasks.length > 0;
  const openExceptionReviewCount = exceptionReviewTasks.filter((task) => task.status === 'open').length;
  const inProgressExceptionReviewCount = exceptionReviewTasks.filter((task) => task.status === 'in_progress').length;
  const completedExceptionReviewCount = exceptionReviewTasks.filter((task) => task.status === 'completed').length;
  const deferredExceptionReviewCount = exceptionReviewTasks.filter((task) => task.status === 'deferred').length;
  const closedExceptionReviewTasks = exceptionReviewTasks.filter((task) => ['completed', 'deferred'].includes(task.status));
  const activeExceptionReviewCount = exceptionReviewTasks.filter((task) => ['open', 'in_progress'].includes(task.status)).length;
  const exceptionReviewStatusUpdateCount = auditSignalsByType(
    state.auditEvents,
    auditExceptionReviewStatusUpdateEventTypes
  ).length;
  const exceptionAuditSignals = state.auditEvents.filter((event) =>
    auditEventMatches(event, auditExceptionReviewEventTypes) ||
    auditEventMatches(event, auditExceptionReviewStatusUpdateEventTypes)
  );
  const versionReferencedExceptionSignalCount = exceptionAuditSignals.filter(hasBackendVersionReference).length;
  const unversionedExceptionSignalCount = exceptionAuditSignals.length - versionReferencedExceptionSignalCount;
  const closedExceptionReviewerGroups = uniqueSortedExceptionReviewerGroups(
    closedExceptionReviewTasks.map((task) => task.assignedGroup)
  );
  const exceptionAdministrationNextStep =
    activeExceptionReviewCount > 0
      ? 'cerrar o diferir el caso desde controles owner y conservar auditoría autorizada'
      : 'mantener resultado cerrado y consultar auditoría autorizada si hay nueva señal';
  const exceptionReviewerGroups = uniqueSortedExceptionReviewerGroups(
    exceptionReviewTasks.map((task) => task.assignedGroup)
  );
  const usingServerTaskQueue = Boolean(state.taskQueue);
  const tasks = usingServerTaskQueue
    ? serverTasks.map((task, index) => serverTaskPresentation(task, documentId, index))
    : [];

  const handleExceptionReviewUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!primaryExceptionReviewTask) {
      return;
    }

    setExceptionReviewUpdating(true);
    setExceptionReviewUpdateMessage('');
    try {
      const result = await updateDocumentTaskStatusInAPI(documentId, primaryExceptionReviewTask.taskId, {
        status: exceptionReviewStatus,
        assignedGroup: exceptionReviewGroup,
      });
      setState((previous) => ({
        ...previous,
        taskQueue: previous.taskQueue
          ? {
              ...previous.taskQueue,
              tasks: previous.taskQueue.tasks.map((task) =>
                task.taskId === primaryExceptionReviewTask.taskId
                  ? {
                      ...task,
                      status: result.task.status,
                      assignedGroup: result.task.assignedGroup,
                      roles: undefined,
                      documentVersionId: undefined,
                      evidenceGap: undefined,
                    }
                  : task
              ),
            }
          : previous.taskQueue,
      }));
      setExceptionReviewUpdateMessage('Estado de revisión actualizado');
    } catch (updateError) {
      setExceptionReviewUpdateMessage(GENERIC_EXCEPTION_REVIEW_UPDATE_ERROR);
    } finally {
      setExceptionReviewUpdating(false);
    }
  };

  const handleLifecycleTransitionUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLifecycleUpdating(true);
    setLifecycleUpdateMessage('');
    try {
      const updatedDocument = await transitionDocumentInAPI(
        documentId,
        lifecycleNextStatus,
        lifecycleReason.trim() || undefined
      );
      setState((previous) => ({
        ...previous,
        document: updatedDocument,
      }));
      setLifecycleUpdateMessage('Estado de ciclo de vida actualizado');
      setLifecycleReason('');
      setLifecycleReloadRequired(false);
    } catch (updateError) {
      const lifecycleConflict = isDocumentLifecycleStateChangedError(updateError);
      const lifecyclePersistenceUncertain = isAppendOnlyLifecyclePersistenceUnavailableError(updateError);
      if (lifecycleConflict || lifecyclePersistenceUncertain) {
        try {
          const [latestDocument, latestTaskQueue] = await Promise.all([
            getDocumentFromAPI(documentId),
            getDocumentTaskQueueFromAPI(documentId).catch(() => undefined),
          ]);
          setState((previous) => ({
            ...previous,
            document: latestDocument,
            taskQueue: latestTaskQueue,
          }));
          setLifecycleReloadRequired(false);
          setLifecycleUpdateMessage(
            lifecycleConflict
              ? LIFECYCLE_CONFLICT_UPDATE_MESSAGE
              : LIFECYCLE_PERSISTENCE_UNCERTAIN_MESSAGE
          );
        } catch (_reloadError) {
          setState((previous) => ({ ...previous, taskQueue: undefined }));
          setLifecycleReloadRequired(true);
          setLifecycleUpdateMessage(
            lifecycleConflict
              ? LIFECYCLE_CONFLICT_RELOAD_ERROR
              : LIFECYCLE_PERSISTENCE_UNCERTAIN_RELOAD_ERROR
          );
        }
      } else {
        setLifecycleUpdateMessage(GENERIC_LIFECYCLE_UPDATE_ERROR);
      }
    } finally {
      setLifecycleUpdating(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Panel de evidencias CMR/eCMR</h1>
          <p className="text-sm text-gray-600">Resumen role-aware de evidencias backend para documento API {documentId || 'desconocido'}.</p>
        </div>
        <Link to="/" className="text-blue-600 hover:text-blue-900 underline">Volver a documentos</Link>
      </div>

      <div className="mb-4 rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-blue-800">
        <p className="font-semibold">Sistema de registro backend</p>
        <p>Este panel resume evidencias sanitizadas disponibles en la API. Es un baseline técnico parcial, no una afirmación de cumplimiento legal total.</p>
      </div>

      <div className="mb-4 rounded border-l-4 border-gray-500 bg-gray-50 p-4 text-gray-800">
        <p className="font-semibold">Legado local/offline no soportado como sistema de registro</p>
        <p>Las referencias locales u offline son superficies heredadas de transición; solo la evidencia backend sanitizada puede operar como sistema de registro eCMR.</p>
      </div>

      {loading && <p>Cargando panel de evidencias...</p>}
      {error && <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{error}</p>}

      {!loading && !error && (
        <>
          <section className="mb-4 rounded border-l-4 border-indigo-500 bg-indigo-50 p-4 text-indigo-900">
            <h2 className="text-lg font-semibold">Cola de tareas role-aware</h2>
            <p className="mt-1 text-sm">
              {usingServerTaskQueue
                ? 'Tareas server-authored desde la API; baseline técnico parcial, no asesoría legal.'
                : 'Cola backend no disponible; no se generan acciones locales como sustituto de la política server-authored.'}
            </p>
            {tasks.length > 0 ? (
              <ul className="mt-3 space-y-2 text-sm">
                {tasks.map((task) => {
                  const metadataSummary = task.sourceTask ? serverTaskMetadataSummary(task.sourceTask) : '';

                  return (
                    <li key={task.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        {task.label}
                        {usingServerTaskQueue && metadataSummary && (
                          <span className="mt-1 block text-xs font-normal">
                            {metadataSummary}
                          </span>
                        )}
                      </span>
                      <Link to={task.href} className="font-medium underline">{task.actionLabel}</Link>
                    </li>
                  );
                })}
              </ul>
            ) : !usingServerTaskQueue ? (
              <p className="mt-2 text-sm">Reintenta la cola de tareas backend antes de ejecutar acciones role-aware.</p>
            ) : (
              <p className="mt-2 text-sm">Sin tareas role-aware pendientes detectadas desde la política backend cargada.</p>
            )}
          </section>

          {activeTransportLifecycleWorkflowTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-teal-500 bg-teal-50 p-4 text-teal-900">
              <h2 className="text-lg font-semibold">Flujo de transporte server-authored</h2>
              <p className="mt-1 text-sm">
                {countLabel(activeTransportLifecycleWorkflowTasks.length, 'tarea de progresión pendiente', 'tareas de progresión pendientes')} desde política backend sanitizada.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="font-medium">Aceptación del transportista</dt>
                  <dd>{countLabel(carrierAcceptanceWorkflowCount, 'tarea de aceptación del transportista', 'tareas de aceptación del transportista')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Inicio de tránsito</dt>
                  <dd>{countLabel(transitStartWorkflowCount, 'tarea de inicio de tránsito', 'tareas de inicio de tránsito')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Confirmación de entrega</dt>
                  <dd>{countLabel(deliveryConfirmationWorkflowCount, 'tarea de confirmación de entrega', 'tareas de confirmación de entrega')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia de versión</dt>
                  <dd>
                    {transportLifecycleHasBackendVersionReference
                      ? 'referencia backend disponible sin exponer identificadores crudos'
                      : 'sin referencia backend versionada disponible'}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">
                Baseline técnico parcial: agrupa progresión de transporte sin mostrar etiquetas, brechas, roles, versiones, metadatos, rutas con tokens ni detalles operativos crudos.
              </p>
              <Link to={transportLifecycleHref} className="mt-3 inline-block text-sm font-medium underline">Abrir flujo de transporte seguro</Link>
            </section>
          )}

          {activeComplianceGovernanceWorkflowTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-violet-500 bg-violet-50 p-4 text-violet-900">
              <h2 className="text-lg font-semibold">Gobernanza de procedimientos, firmas y retención</h2>
              <p className="mt-1 text-sm">
                {countLabel(activeComplianceGovernanceWorkflowTasks.length, 'tarea de gobernanza pendiente', 'tareas de gobernanza pendientes')} desde política backend sanitizada.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="font-medium">Procedimientos</dt>
                  <dd>{countLabel(procedureGovernanceWorkflowCount, 'tarea de procedimientos requiere acción', 'tareas de procedimientos requieren acción')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Firma server-side</dt>
                  <dd>{countLabel(signatureGovernanceWorkflowCount, 'tarea de firma requiere acción', 'tareas de firma requieren acción')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Retención/legal hold</dt>
                  <dd>{countLabel(retentionGovernanceWorkflowCount, 'tarea de retención requiere revisión', 'tareas de retención requieren revisión')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia backend</dt>
                  <dd>{complianceGovernanceReferenceStatus}</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">
                Baseline técnico parcial: agrupa tareas de procedimientos, firma y retención sin mostrar etiquetas, brechas, roles, versiones crudas, actores, razones privadas, digests, provider metadata, storage, XML, URLs, tokens ni payloads.
              </p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm font-medium">
                <Link to={complianceGovernanceWorkflowHref} className="underline">Abrir flujo de gobernanza seguro</Link>
                <Link to={complianceGovernanceRetentionHref} className="underline">Abrir retención autorizada</Link>
              </div>
            </section>
          )}

          {activeArchiveReadinessTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-emerald-500 bg-emerald-50 p-4 text-emerald-900">
              <h2 className="text-lg font-semibold">Preparación de archivo inmutable</h2>
              <p className="mt-1 text-sm">
                {countLabel(activeArchiveReadinessTasks.length, 'tarea de archivo pendiente', 'tareas de archivo pendientes')} desde política backend sanitizada.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="font-medium">XML final</dt>
                  <dd>{countLabel(finalXmlArchiveReadinessCount, 'tarea para XML final', 'tareas para XML final')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Manifiesto backend</dt>
                  <dd>{countLabel(evidenceManifestArchiveReadinessCount, 'tarea para manifiesto de evidencias', 'tareas para manifiesto de evidencias')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia de versión</dt>
                  <dd>
                    {archiveReadinessHasBackendVersionReference
                      ? 'referencia backend disponible sin exponer identificadores crudos'
                      : 'sin referencia backend versionada disponible'}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">
                Baseline técnico parcial: guía el archivo de XML final/manifiesto, no prueba WORM/legal hold independiente.
              </p>
              <Link to={archiveReadinessHref} className="mt-3 inline-block text-sm font-medium underline">Abrir exportación segura</Link>
            </section>
          )}

          {activeOperationalAcknowledgementTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-cyan-500 bg-cyan-50 p-4 text-cyan-900">
              <h2 className="text-lg font-semibold">Revisión operativa y reconocimientos</h2>
              <p className="mt-1 text-sm">
                {countLabel(activeOperationalAcknowledgementTasks.length, 'tarea operativa pendiente', 'tareas operativas pendientes')} desde política backend sanitizada.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <dt className="font-medium">Toma de cargo</dt>
                  <dd>{countLabel(carrierTakeoverCheckCount, 'tarea de comprobación al hacerse cargo', 'tareas de comprobación al hacerse cargo')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Comprobaciones solicitadas</dt>
                  <dd>{countLabel(carrierRequestedCheckResultCount, 'tarea de resultado solicitado', 'tareas de resultado solicitado')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Revisión de reservas</dt>
                  <dd>{countLabel(carrierReservationReviewCount, 'tarea de revisión de reservas', 'tareas de revisión de reservas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reservas del transportista</dt>
                  <dd>{countLabel(carrierReservationAcknowledgementCount, 'tarea de reservas del transportista', 'tareas de reservas del transportista')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reservas de recepción</dt>
                  <dd>{countLabel(deliveryReservationAcknowledgementCount, 'tarea de reservas de recepción', 'tareas de reservas de recepción')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Enmiendas</dt>
                  <dd>{countLabel(amendmentAcknowledgementCount, 'tarea de enmiendas pendientes', 'tareas de enmiendas pendientes')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia de versión</dt>
                  <dd>
                    {operationalAcknowledgementHasBackendVersionReference
                      ? 'referencia backend disponible sin exponer identificadores crudos'
                      : 'sin referencia backend versionada disponible'}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">
                Baseline técnico parcial: agrupa reconocimientos/revisiones operativas sin mostrar etiquetas, brechas, roles, versiones o detalles crudos.
              </p>
              <Link to={operationalAcknowledgementHref} className="mt-3 inline-block text-sm font-medium underline">Abrir revisión operativa segura</Link>
            </section>
          )}

          {activeSignatureVerificationReviewTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-sky-500 bg-sky-50 p-4 text-sky-900">
              <h2 className="text-lg font-semibold">Revisión de verificación de firmas</h2>
              <p className="mt-1 text-sm">
                {countLabel(activeSignatureVerificationReviewTasks.length, 'tarea de verificación de firma pendiente', 'tareas de verificación de firma pendientes')} desde política backend sanitizada.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
                <div>
                  <dt className="font-medium">Política de firma</dt>
                  <dd>{countLabel(signatureVerificationReviewPolicyCount, 'tarea requiere revisión de política de firma', 'tareas requieren revisión de política de firma')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia de versión</dt>
                  <dd>
                    {signatureVerificationReviewHasBackendVersionReference
                      ? 'referencia backend disponible sin exponer identificadores crudos'
                      : 'sin referencia backend versionada disponible'}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">Alcance</dt>
                  <dd>baseline técnico parcial de verificación</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">
                Agrupa revisión de firmas sin mostrar etiquetas, brechas, roles, versiones, firmantes, métodos, digests, provider metadata ni detalles crudos.
              </p>
              <Link to={signatureVerificationReviewHref} className="mt-3 inline-block text-sm font-medium underline">Abrir revisión segura de firmas</Link>
            </section>
          )}

          {activeDownloadCompletionReviewTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-900">
              <h2 className="text-lg font-semibold">Revisión de completitud de descargas</h2>
              <p className="mt-1 text-sm">
                {countLabel(activeDownloadCompletionReviewTasks.length, 'tarea de revisión de descarga pendiente', 'tareas de revisión de descarga pendientes')} desde política backend sanitizada.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-5">
                <div>
                  <dt className="font-medium">XML final</dt>
                  <dd>{countLabel(finalXmlDownloadCompletionReviewCount, 'tarea de XML final requiere revisión de descarga', 'tareas de XML final requieren revisión de descarga')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Manifiesto</dt>
                  <dd>{countLabel(evidenceManifestDownloadCompletionReviewCount, 'tarea de manifiesto requiere revisión de descarga', 'tareas de manifiesto requieren revisión de descarga')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Adjuntos suplementarios</dt>
                  <dd>{countLabel(supplementaryAttachmentDownloadCompletionReviewCount, 'tarea de adjunto suplementario requiere revisión de descarga', 'tareas de adjunto suplementario requieren revisión de descarga')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia backend</dt>
                  <dd>{downloadCompletionReviewReferenceStatus}</dd>
                </div>
                <div>
                  <dt className="font-medium">Exportación</dt>
                  <dd>{downloadCompletionReviewExportStatus}</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">
                Baseline técnico parcial: agrupa revisión de confirmaciones de descarga sin mostrar etiquetas, brechas, roles, versiones crudas, storage, object-version IDs, digests, URLs, tokens, XML, request metadata, downloader IDs ni payloads.
              </p>
              <Link to={downloadCompletionReviewHref} className="mt-3 inline-block text-sm font-medium underline">Abrir exportación segura para revisión de descargas</Link>
            </section>
          )}

          {completedServerTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-green-500 bg-green-50 p-4 text-green-900">
              <h2 className="text-lg font-semibold">Tareas de gobernanza completadas</h2>
              <p className="mt-1 text-sm">{countLabel(completedServerTasks.length, 'tarea completada por política backend', 'tareas completadas por política backend')}</p>
              <ul className="mt-3 space-y-2 text-sm">
                {completedServerTasks.map((task, index) => (
                  <li key={`completed-server-task-${index}`}>
                    <span className="font-medium">{serverTaskPresentation(task, documentId, index).label}</span>
                    <span className="mt-1 block text-xs font-normal">
                      estado saneado: {safeServerTaskStatusLabel(task.status)}
                      {hasBackendVersionReference(task) && ' · referencia de versión backend disponible'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {auditReviewWorkbench.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-orange-500 bg-orange-50 p-4 text-orange-900">
              <h2 className="text-lg font-semibold">Workbench de revisión de auditoría</h2>
              <p className="mt-1 text-sm">
                {countLabel(auditReviewPriorityCount, 'prioridad agrupada', 'prioridades agrupadas')} desde auditoría backend sanitizada.
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {auditReviewWorkbench.map((category) => (
                  <li key={category.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      <span className="font-medium">{category.label}</span>
                      <span className="mt-1 block text-xs font-normal">
                        {countLabel(category.count, 'evento requiere revisión', 'eventos requieren revisión')}
                      </span>
                    </span>
                    <span className="font-medium">{category.nextActionLabel}</span>
                  </li>
                ))}
              </ul>
              <Link to={`/audit/${documentId}`} className="mt-3 inline-block text-sm font-medium underline">Abrir trazabilidad detallada</Link>
            </section>
          )}

          {hasExceptionReview && (
            <section className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-4 text-red-900">
              <h2 className="text-lg font-semibold">Panel de revisión de excepciones</h2>
              <p className="mt-1 text-sm">Resumen sanitizado para priorizar excepción; no muestra tipos de evento, IDs, roles, versiones ni detalles crudos.</p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Críticas</dt>
                  <dd>{countLabel(exceptionSummary.criticalCount, 'excepción crítica', 'excepciones críticas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Operativas</dt>
                  <dd>{countLabel(exceptionSummary.operationalCount, 'excepción operativa', 'excepciones operativas')}</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm font-medium">Priorizar investigación de integridad y validación antes de cierre/archivo</p>
              <Link to={`/audit/${documentId}`} className="mt-3 inline-block text-sm font-medium underline">Abrir trazabilidad para revisión de excepciones</Link>
            </section>
          )}

          {exceptionReviewTasks.length > 0 && (
            <section className="mb-4 rounded border-l-4 border-purple-500 bg-purple-50 p-4 text-purple-900">
              <h2 className="text-lg font-semibold">Estado de revisión de excepciones</h2>
              <p className="mt-1 text-sm">
                {countLabel(exceptionReviewTasks.length, 'tarea de excepción asignada por política backend', 'tareas de excepción asignadas por política backend')}.
              </p>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="font-medium">Abiertas</dt>
                  <dd>{countLabel(openExceptionReviewCount, 'abierta', 'abiertas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">En progreso</dt>
                  <dd>{countLabel(inProgressExceptionReviewCount, 'en progreso', 'en progreso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Completadas</dt>
                  <dd>{countLabel(completedExceptionReviewCount, 'completada', 'completadas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Asignación</dt>
                  <dd>{countLabel(exceptionReviewerGroups.length, 'grupo responsable', 'grupos responsables')}</dd>
                </div>
              </dl>
              <p className="mt-3 text-sm">Las etiquetas, brechas, roles, versiones y detalles crudos de tareas de excepción se mantienen fuera de este panel.</p>
              {hasExceptionCoverageSummary && (
                <div className="mt-3 rounded border border-purple-200 bg-white/60 p-3 text-sm">
                  <h3 className="font-semibold">Cobertura de revisión de excepciones</h3>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-3">
                    <div>
                      <dt className="font-medium">Riesgo auditado</dt>
                      <dd>{countLabel(auditedExceptionCategoryCount, 'categoría con riesgo auditado', 'categorías con riesgo auditado')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Tareas backend</dt>
                      <dd>{countLabel(exceptionReviewTasks.length, 'tarea server-authored de revisión', 'tareas server-authored de revisión')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Seguimiento activo</dt>
                      <dd>{countLabel(inProgressExceptionReviewCount, 'tarea en progreso', 'tareas en progreso')}</dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs font-medium">cobertura parcial por baseline técnico; no expone eventos, roles, versiones ni detalles crudos.</p>
                </div>
              )}
              {exceptionAuditSignals.length > 0 && (
                <div className="mt-3 rounded border border-purple-200 bg-white/60 p-3 text-sm">
                  <h3 className="font-semibold">Contexto auditado sanitizado</h3>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-3">
                    <div>
                      <dt className="font-medium">Señales versionadas</dt>
                      <dd>{countLabel(versionReferencedExceptionSignalCount, 'señal con referencia de versión backend', 'señales con referencia de versión backend')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Señales sin versión</dt>
                      <dd>{countLabel(unversionedExceptionSignalCount, 'señal sin referencia de versión', 'señales sin referencia de versión')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Actualizaciones</dt>
                      <dd>{countLabel(exceptionReviewStatusUpdateCount, 'actualización de revisión registrada en auditoría', 'actualizaciones de revisión registradas en auditoría')}</dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs font-medium">No se muestran filas de auditoría, IDs, roles, grupos, versiones ni detalles crudos.</p>
                </div>
              )}
              {exceptionAuditSignals.length > 0 && exceptionReviewTasks.length > 0 && (
                <div className="mt-3 rounded border border-purple-200 bg-white/60 p-3 text-sm">
                  <h3 className="font-semibold">Checklist de traspaso de caso de excepción</h3>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-3">
                    <div>
                      <dt className="font-medium">Paquete auditado</dt>
                      <dd>{countLabel(exceptionAuditSignals.length, 'señal auditada sanitizada', 'señales auditadas sanitizadas')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Revisión activa</dt>
                      <dd>{countLabel(activeExceptionReviewCount, 'tarea activa de revisión', 'tareas activas de revisión')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Historial</dt>
                      <dd>{countLabel(exceptionReviewStatusUpdateCount, 'actualización de historial disponible', 'actualizaciones de historial disponibles')}</dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs font-medium">Preparar traspaso a revisión responsable con auditoría autorizada.</p>
                  <p className="mt-1 text-xs font-medium">No incluye grupos, roles, versiones, comentarios, XML, storage keys, digests ni payloads.</p>
                </div>
              )}
              {exceptionAuditSignals.length > 0 && exceptionReviewTasks.length > 0 && (
                <div className="mt-3 rounded border border-purple-200 bg-white/60 p-3 text-sm">
                  <h3 className="font-semibold">Preparación administrativa de excepción</h3>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-4">
                    <div>
                      <dt className="font-medium">Señales</dt>
                      <dd>{countLabel(exceptionAuditSignals.length, 'señal lista para revisión', 'señales listas para revisión')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Coordinación</dt>
                      <dd>{countLabel(activeExceptionReviewCount, 'tarea activa coordinada', 'tareas activas coordinadas')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Resultados</dt>
                      <dd>{countLabel(closedExceptionReviewTasks.length, 'resultado cerrado disponible', 'resultados cerrados disponibles')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Historial</dt>
                      <dd>{countLabel(exceptionReviewStatusUpdateCount, 'actualización histórica disponible', 'actualizaciones históricas disponibles')}</dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs font-medium">Siguiente paso: {exceptionAdministrationNextStep}</p>
                  <p className="mt-1 text-xs font-medium">Resumen administrativo sanitizado; no expone etiquetas privadas, grupos, roles, versiones, comentarios, URLs, tokens, XML, storage keys, digests ni payloads.</p>
                </div>
              )}
              {closedExceptionReviewTasks.length > 0 && (
                <div className="mt-3 rounded border border-purple-200 bg-white/60 p-3 text-sm">
                  <h3 className="font-semibold">Resumen de cierre de excepciones</h3>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-3">
                    <div>
                      <dt className="font-medium">Completadas</dt>
                      <dd>{countLabel(completedExceptionReviewCount, 'revisión completada', 'revisiones completadas')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Diferidas</dt>
                      <dd>{countLabel(deferredExceptionReviewCount, 'revisión diferida', 'revisiones diferidas')}</dd>
                    </div>
                    <div>
                      <dt className="font-medium">Grupos</dt>
                      <dd>{countLabel(closedExceptionReviewerGroups.length, 'grupo responsable', 'grupos responsables')}</dd>
                    </div>
                  </dl>
                  <p className="mt-2 text-xs font-medium">Resultado resumido por política backend; consultar auditoría para trazabilidad autorizada.</p>
                </div>
              )}
              {exceptionReviewStatusUpdateCount > 0 && (
                <div className="mt-3 rounded border border-purple-200 bg-white/60 p-3 text-sm">
                  <h3 className="font-semibold">Historial sanitizado de revisión</h3>
                  <p className="mt-2">
                    {countLabel(exceptionReviewStatusUpdateCount, 'actualización de estado registrada', 'actualizaciones de estado registradas')}
                  </p>
                  <p className="mt-2 text-xs font-medium">
                    La trazabilidad detallada permanece en auditoría autorizada; este panel no expone eventos, grupos, versiones, comentarios ni detalles crudos.
                  </p>
                </div>
              )}
              {primaryExceptionReviewTask && (
                <form onSubmit={handleExceptionReviewUpdate} className="mt-3 grid gap-3 rounded border border-purple-200 bg-white/60 p-3 text-sm sm:grid-cols-3">
                  <label className="flex flex-col gap-1">
                    <span className="font-medium">Estado de revisión</span>
                    <select
                      value={exceptionReviewStatus}
                      onChange={(event) => setExceptionReviewStatus(event.target.value as 'open' | 'in_progress' | 'completed')}
                      className="rounded border border-purple-300 p-2"
                    >
                      <option value="open">abierta</option>
                      <option value="in_progress">en progreso</option>
                      <option value="completed">completada</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="font-medium">Grupo asignado</span>
                    <select
                      value={exceptionReviewGroup}
                      onChange={(event) => setExceptionReviewGroup(event.target.value)}
                      className="rounded border border-purple-300 p-2"
                    >
                      <option value="compliance_review">compliance_review</option>
                      <option value="legal_review">legal_review</option>
                      <option value="operations_review">operations_review</option>
                    </select>
                  </label>
                  <div className="flex flex-col justify-end gap-1">
                    <button type="submit" disabled={exceptionReviewUpdating} className="rounded bg-purple-700 px-3 py-2 font-medium text-white disabled:opacity-60">
                      Actualizar revisión de excepción
                    </button>
                    {exceptionReviewUpdateMessage && <p className="text-xs font-medium">{exceptionReviewUpdateMessage}</p>}
                  </div>
                </form>
              )}
              <Link to={`/audit/${documentId}`} className="mt-3 inline-block text-sm font-medium underline">Abrir trazabilidad para gestión de excepciones</Link>
            </section>
          )}

          <div className="grid gap-4 md:grid-cols-2">
          <EvidenceCard
            title="Versión actual"
            available={Boolean(currentVersion)}
            status={currentVersion ? (currentVersion.versionNumber !== undefined ? `Versión ${currentVersion.versionNumber}` : 'Versión backend disponible') : 'Sin versión backend'}
          >
            {currentVersion?.digestAlgorithm && <p>Algoritmo de digest: {currentVersion.digestAlgorithm}</p>}
          </EvidenceCard>

          <EvidenceCard
            title="Auditoría backend"
            available={!state.auditAccessIssue && state.auditEvents.length > 0}
            status={
              state.auditAccessIssue === 'state_changed'
                ? 'Recarga requerida por cambio concurrente'
                : state.auditAccessIssue === 'persistence_unavailable'
                  ? 'Evidencia de acceso temporalmente no disponible'
                  : state.auditAccessIssue === 'unavailable'
                    ? 'Auditoria temporalmente no disponible'
                  : countLabel(state.auditEvents.length, 'evento sanitizado', 'eventos sanitizados')
            }
          >
            <Link to={`/audit/${documentId}`} className="underline">Ver trazabilidad detallada</Link>
          </EvidenceCard>

          <EvidenceCard
            title="XML final inmutable"
            available={Boolean(state.finalXmlArchive?.archiveReference)}
            status={
              state.finalXmlArchive?.archiveReference
                ? 'Referencia verificada por backend'
                : finalXmlSupplementaryIntegrityRequired
                  ? 'Adjuntos suplementarios requieren enlace de integridad'
                  : 'No disponible'
            }
          >
            {state.finalXmlArchive?.archiveReference?.retentionMode && <p>{state.finalXmlArchive.archiveReference.retentionMode}</p>}
            {finalXmlSupplementaryIntegrityRequired ? (
              <>
                <p>XML final bloqueado por documentos suplementarios sin SHA-256, almacenamiento backend o referencia a la version actual.</p>
                <Link to={`/attachments/${documentId}`} className="underline">Gestionar adjuntos</Link>
              </>
            ) : (
              <Link to={`/final-xml/${documentId}`} className="underline">Abrir XML final</Link>
            )}
          </EvidenceCard>

          <EvidenceCard
            title="Adjuntos suplementarios"
            available={state.attachments.length > 0}
            status={countLabel(state.attachments.length, 'referencia sanitizada', 'referencias sanitizadas')}
          >
            <Link to={`/attachments/${documentId}`} className="underline">Gestionar adjuntos</Link>
          </EvidenceCard>

          {hasDeliveryAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de prueba de entrega"
              available={Boolean(deliveryConfirmation)}
              status="Resumen administrativo de entrega sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Confirmación</dt>
                  <dd>{countLabel(deliveryConfirmation ? 1 : 0, 'confirmación de entrega revisable', 'confirmaciones de entrega revisables')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reservas de recepción</dt>
                  <dd>{countLabel(deliveryReservationCount, 'reserva de entrega registrada', 'reservas de entrega registradas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Versión backend</dt>
                  <dd>{countLabel(deliveryVersionReferenceCount, 'referencia de versión backend disponible', 'referencias de versión backend disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de entrega</dt>
                  <dd>{countLabel(deliveryAuditSignals.length, 'señal auditada de entrega', 'señales auditadas de entrega')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparación administrativa sobre baseline técnico parcial de entrega; no sustituye prueba legal independiente, WORM ni resolución de reservas.</p>
              <p className="mt-1 text-xs font-medium">No expone IDs, destinatarios, ubicaciones, actores, firmas, reservas, versiones, eventos crudos, storage, XML, digests, tokens ni payloads.</p>
              <Link to={`/evidence/${documentId}`} className="mt-2 inline-block underline">Abrir evidencias operativas de entrega</Link>
            </EvidenceCard>
          )}

          {hasDeliveryAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de traspaso de prueba de entrega"
              available={Boolean(deliveryConfirmation) && deliveryAuditSignals.length > 0}
              status="Traspaso de entrega sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Confirmación</dt>
                  <dd>{countLabel(deliveryConfirmation ? 1 : 0, 'confirmación lista para traspaso', 'confirmaciones listas para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Firma de entrega</dt>
                  <dd>{countLabel(deliverySignatureCount, 'firma de entrega disponible', 'firmas de entrega disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reservas</dt>
                  <dd>{countLabel(deliveryReservationCount, 'reserva de entrega para traspaso', 'reservas de entrega para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(deliveryAuditSignals.length, 'señal auditada disponible para traspaso de entrega', 'señales auditadas disponibles para traspaso de entrega')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Checklist operativo para traspaso de prueba de entrega; baseline técnico parcial de entrega, no certificación legal ni prueba S3/transport independiente.</p>
              <p className="mt-1 text-xs font-medium">Muestra solo agregados: no expone receptor, ubicación, firmantes, roles, versiones, reservas, digests, provider metadata, eventos ni detalles privados.</p>
              <Link to={`/evidence/${documentId}`} className="mt-2 inline-block underline">Abrir traspaso operativo de entrega</Link>
            </EvidenceCard>
          )}

          {deliveryReservationCount > 0 && (
            <EvidenceCard
              title="Preparación de revisión de reservas de entrega"
              available={deliveryReservationCount > 0 && deliveryAuditSignals.length > 0}
              status="Revisión de reservas de recepción sanitizada"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Reservas de recepción</dt>
                  <dd>{countLabel(deliveryReservationCount, 'reserva de recepción requiere revisión', 'reservas de recepción requieren revisión')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría disponible</dt>
                  <dd>{countLabel(deliveryAuditSignals.length, 'señal auditada disponible para revisión de entrega', 'señales auditadas disponibles para revisión de entrega')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación backend</dt>
                  <dd>{countLabel(deliveryVersionReferenceCount, 'referencia versionada para revisión de reserva de entrega', 'referencias versionadas para revisión de reserva de entrega')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Siguiente paso: coordinar aceptación o disputa sin exponer detalles privados de recepción.</p>
              <p className="mt-1 text-xs font-medium">Resumen de baseline técnico parcial de entrega; no expone destinatarios, ubicaciones, reservas, firmas, actores, versiones, XML, storage, digests, URLs, tokens ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir revisión autorizada de reservas de entrega</Link>
            </EvidenceCard>
          )}

          {deliveryAuditSignals.length > 0 && (
            <EvidenceCard
              title="Historial auditado de prueba de entrega"
              available={deliveryAuditSignals.length > 0}
              status="Historial de entrega sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales históricas</dt>
                  <dd>{countLabel(deliveryAuditSignals.length, 'señal histórica de entrega', 'señales históricas de entrega')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedDeliveryAuditSignalCount, 'señal de entrega con referencia versionada', 'señales de entrega con referencia versionada')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación pendiente</dt>
                  <dd>{countLabel(unversionedDeliveryAuditSignalCount, 'señal de entrega pendiente de reconciliación versionada', 'señales de entrega pendientes de reconciliación versionada')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Resumen histórico agregado de entrega para revisión autorizada; no sustituye evidencia append-only/WORM ni validación legal de recepción.</p>
              <p className="mt-1 text-xs font-medium">No expone IDs de entrega, destinatarios, ubicaciones, actores, versiones, eventos crudos, storage, XML, digests, tokens ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir historial autorizado de entrega</Link>
            </EvidenceCard>
          )}

          {hasCarrierReservationAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de reservas del transportista"
              available={carrierReservations.length > 0}
              status="Resumen administrativo de reservas sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Reservas</dt>
                  <dd>{countLabel(carrierReservations.length, 'reserva revisable', 'reservas revisables')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Categorías</dt>
                  <dd>{countLabel(carrierReservationCategories.length, 'categoría de reserva registrada', 'categorías de reserva registradas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Versión backend</dt>
                  <dd>{countLabel(versionedCarrierReservationCount, 'referencia de versión backend disponible', 'referencias de versión backend disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de reservas</dt>
                  <dd>{countLabel(carrierReservationAuditSignals.length, 'señal auditada de reservas del transportista', 'señales auditadas de reservas del transportista')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparación administrativa sobre baseline técnico parcial de reservas; no sustituye disputa/aceptación legal ni evidencia append-only/WORM.</p>
              <p className="mt-1 text-xs font-medium">No expone IDs de reserva, grounds, referencias de bulto, valores observados/esperados, actores, versiones, tipos de evento, IDs de auditoría ni detalles crudos.</p>
              <Link to={`/evidence/${documentId}`} className="mt-2 inline-block underline">Abrir evidencias operativas de reservas</Link>
            </EvidenceCard>
          )}

          {hasCarrierReservationAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de traspaso de reservas del transportista"
              available={carrierReservations.length > 0 && carrierReservationAuditSignals.length > 0}
              status="Traspaso de reservas sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Reservas</dt>
                  <dd>{countLabel(carrierReservations.length, 'reserva lista para traspaso', 'reservas listas para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Categorías</dt>
                  <dd>{countLabel(carrierReservationCategories.length, 'categoría lista para traspaso', 'categorías listas para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(versionedCarrierReservationCount, 'referencia versionada preparada para traspaso de reservas', 'referencias versionadas preparadas para traspaso de reservas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(carrierReservationAuditSignals.length, 'señal auditada disponible para traspaso de reservas', 'señales auditadas disponibles para traspaso de reservas')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Checklist operativo para traspaso de reservas del transportista; baseline técnico parcial de reservas, no resolución legal de disputas.</p>
              <p className="mt-1 text-xs font-medium">No expone IDs, grounds, bultos, valores, actores, versiones, eventos crudos, storage, XML, digests ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada de reservas</Link>
            </EvidenceCard>
          )}

          {carrierReservationAuditSignals.length > 0 && (
            <EvidenceCard
              title="Historial auditado de reservas del transportista"
              available={carrierReservationAuditSignals.length > 0}
              status="Historial de reservas sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales históricas</dt>
                  <dd>{countLabel(carrierReservationAuditSignals.length, 'señal histórica de reservas', 'señales históricas de reservas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedCarrierReservationAuditSignalCount, 'señal de reserva con referencia versionada', 'señales de reserva con referencia versionada')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación pendiente</dt>
                  <dd>{countLabel(unversionedCarrierReservationAuditSignalCount, 'señal de reserva pendiente de reconciliación versionada', 'señales de reserva pendientes de reconciliación versionada')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Resumen histórico agregado para revisión autorizada; no resuelve aceptación legal de reservas ni sustituye evidencia append-only/WORM.</p>
              <p className="mt-1 text-xs font-medium">No expone IDs de reserva, grounds, bultos, valores, actores, versiones, eventos crudos, storage, XML, digests, tokens ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir historial autorizado de reservas</Link>
            </EvidenceCard>
          )}

          {hasAttachmentAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de adjuntos"
              available={state.attachments.length > 0}
              status="Resumen administrativo de adjuntos sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Referencias</dt>
                  <dd>{countLabel(state.attachments.length, 'referencia de adjunto revisable', 'referencias de adjunto revisables')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Almacenamiento</dt>
                  <dd>{countLabel(externallyStoredAttachmentCount, 'evidencia almacenada externamente', 'evidencias almacenadas externamente')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Digest</dt>
                  <dd>{countLabel(digestAvailableAttachmentCount, 'referencia con digest disponible', 'referencias con digest disponible')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de adjuntos</dt>
                  <dd>{countLabel(attachmentAuditSignals.length, 'señal auditada de adjuntos', 'señales auditadas de adjuntos')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparación administrativa sobre baseline técnico parcial de adjuntos; no sustituye malware scanning, DLP, WORM unificado ni prueba independiente de transferencia.</p>
              <p className="mt-1 text-xs font-medium">No expone nombres de archivo, storage buckets/keys, URLs, IDs crudos de auditoría, digests, XML ni payloads.</p>
              <Link to={`/attachments/${documentId}`} className="mt-2 inline-block underline">Abrir administración de adjuntos</Link>
            </EvidenceCard>
          )}

          {hasAttachmentAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de traspaso de adjuntos"
              available={state.attachments.length > 0 && attachmentAuditSignals.length > 0}
              status="Traspaso de adjuntos sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Referencias</dt>
                  <dd>{countLabel(state.attachments.length, 'referencia lista para traspaso', 'referencias listas para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Digest</dt>
                  <dd>{countLabel(digestAvailableAttachmentCount, 'digest disponible para traspaso', 'digests disponibles para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Almacenamiento externo</dt>
                  <dd>{countLabel(externallyStoredAttachmentCount, 'evidencia externa lista para traspaso', 'evidencias externas listas para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(attachmentAuditSignals.length, 'señal auditada disponible para traspaso de adjuntos', 'señales auditadas disponibles para traspaso de adjuntos')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Checklist operativo para traspaso de adjuntos; no prueba transferencia independiente ni sustituye controles WORM/malware/DLP.</p>
              <p className="mt-1 text-xs font-medium">No expone nombres, buckets, keys, URLs, IDs crudos, digests, XML ni payloads.</p>
              <Link to={`/attachments/${documentId}`} className="mt-2 inline-block underline">Abrir adjuntos para traspaso</Link>
            </EvidenceCard>
          )}

          {attachmentAuditSignals.length > 0 && (
            <EvidenceCard
              title="Historial auditado de adjuntos"
              available={attachmentAuditSignals.length > 0}
              status="Historial de adjuntos sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales históricas</dt>
                  <dd>{countLabel(attachmentAuditSignals.length, 'señal histórica de adjuntos', 'señales históricas de adjuntos')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedAttachmentAuditSignalCount, 'señal de adjunto con referencia versionada', 'señales de adjunto con referencia versionada')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación pendiente</dt>
                  <dd>{countLabel(unversionedAttachmentAuditSignalCount, 'señal de adjunto pendiente de reconciliación versionada', 'señales de adjunto pendientes de reconciliación versionada')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Resumen histórico agregado para revisión autorizada de adjuntos; no sustituye evidencia append-only/WORM, malware scanning, DLP ni prueba independiente de transferencia.</p>
              <p className="mt-1 text-xs font-medium">No expone nombres de archivo, buckets, keys, URLs, IDs de auditoría, tipos de evento crudos, versiones, digests, XML, validación ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir historial autorizado de adjuntos</Link>
            </EvidenceCard>
          )}

          <EvidenceCard
            title="Firmas server-side"
            available={verifiedSignatureCount > 0 && Boolean(state.signatureVerification?.valid)}
            status={verifiedSignatureCount > 0 ? countLabel(verifiedSignatureCount, 'firma verificada', 'firmas verificadas') : 'Sin firmas verificadas'}
          >
            <p className="font-medium">
              {state.signatureVerification
                ? 'Verificación backend de firmas disponible'
                : 'Verificación backend de firmas no disponible'}
            </p>
            <p className="mt-1">
              {`GET /documents/${documentId}/signatures/verify`} · resultado sanitizado sin IDs de firma, digests ni provider metadata.
            </p>
            <p className="mt-1">
              Rutas locales/offline heredadas no generan verificación de sistema de registro; use el flujo backend protegido.
            </p>
            <Link to={`/workflow/${documentId}`} className="mt-2 inline-block underline">Abrir verificación backend de firmas</Link>
          </EvidenceCard>

          {hasSignatureAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de firmas"
              available={signatureEvidenceCount > 0}
              status="Resumen administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Firmas</dt>
                  <dd>{countLabel(signatureEvidenceCount, 'firma revisable', 'firmas revisables')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Métodos</dt>
                  <dd>{countLabel(signatureMethods.length, 'método de firma registrado', 'métodos de firma registrados')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Versión backend</dt>
                  <dd>{countLabel(versionedSignatureEvidenceCount, 'referencia de versión backend disponible', 'referencias de versión backend disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de firma</dt>
                  <dd>{countLabel(signatureAuditSignals.length, 'señal auditada de firma', 'señales auditadas de firma')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Administración de firmas sobre baseline técnico parcial; no sustituye firma avanzada/cualificada ni revisión legal.</p>
              <p className="mt-1 text-xs font-medium">No expone firmantes, digests, provider metadata, motivos, IDs de versión, tipos de evento ni detalles crudos.</p>
              <Link to={`/workflow/${documentId}`} className="mt-2 inline-block underline">Abrir administración de firmas</Link>
            </EvidenceCard>
          )}

          {hasSignatureAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de política de firma"
              available={signatureEvidenceCount > 0 && signatureMethods.length > 0}
              status="Checklist de firma sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Evidencia firmada</dt>
                  <dd>{countLabel(signatureEvidenceCount, 'firma dentro de política', 'firmas dentro de política')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Métodos cubiertos</dt>
                  <dd>{countLabel(signatureMethods.length, 'método cubierto por política', 'métodos cubiertos por política')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencias versionadas</dt>
                  <dd>{countLabel(versionedSignatureEvidenceCount, 'referencia versionada para política', 'referencias versionadas para política')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de política</dt>
                  <dd>{countLabel(signatureAuditSignals.length, 'señal auditada para política', 'señales auditadas para política')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Checklist operativo para revisar si la política interna de firma tiene evidencia suficiente; no sustituye proveedor eIDAS/XAdES ni decisión jurídica.</p>
              <p className="mt-1 text-xs font-medium">Solo muestra agregados: no expone firmantes, métodos crudos, digests, provider metadata, razones privadas, eventos crudos ni IDs de versión.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada de firma</Link>
            </EvidenceCard>
          )}

          {hasSignatureAdministrationEvidence && (
            <EvidenceCard
              title="Historial auditado de firmas"
              available={signatureAuditSignals.length > 0}
              status="Historial de firma sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales históricas</dt>
                  <dd>{countLabel(signatureAuditSignals.length, 'señal histórica de firma', 'señales históricas de firma')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedSignatureAuditSignalCount, 'señal de firma con referencia versionada', 'señales de firma con referencia versionada')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación pendiente</dt>
                  <dd>{countLabel(unversionedSignatureAuditSignalCount, 'señal de firma pendiente de reconciliación versionada', 'señales de firma pendientes de reconciliación versionada')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Resumen agregado para revisión autorizada de firma; no sustituye evidencia eIDAS/XAdES ni historial append-only.</p>
              <p className="mt-1 text-xs font-medium">No expone eventos, IDs, firmantes, roles, versiones, digests, provider metadata, razones privadas ni detalles crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir revisión autorizada de firma</Link>
            </EvidenceCard>
          )}

          {hasSignatureAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de entrega de firmas"
              available={signatureEvidenceCount > 0 && verifiedSignatureCount > 0}
              status="Entrega de firma sanitizada"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Firmas entregables</dt>
                  <dd>{countLabel(signatureEvidenceCount, 'firma lista para entrega', 'firmas listas para entrega')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencias de entrega</dt>
                  <dd>{countLabel(versionedSignatureEvidenceCount, 'referencia versionada para entrega', 'referencias versionadas para entrega')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de entrega</dt>
                  <dd>{countLabel(signatureAuditSignals.length, 'señal auditada para entrega', 'señales auditadas para entrega')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Verificación</dt>
                  <dd>{countLabel(verifiedSignatureCount, 'resultado de verificación disponible', 'resultados de verificación disponibles')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Checklist de baseline técnico parcial de entrega para preparar revisión operativa de firmas; no sustituye proveedor avanzado/cualificado ni asesoría legal.</p>
              <p className="mt-1 text-xs font-medium">Muestra solo agregados: no expone firmantes, métodos crudos, roles, versiones, digests, provider metadata, eventos ni detalles privados.</p>
              <Link to={`/workflow/${documentId}`} className="mt-2 inline-block underline">Abrir flujo de entrega de firmas</Link>
            </EvidenceCard>
          )}

          <EvidenceCard
            title="Manifiesto de evidencias"
            available={manifestArchived}
            status={
              manifestArchived
                ? 'archivado por backend'
                : evidenceManifestSupplementaryIntegrityRequired
                  ? 'Adjuntos suplementarios requieren enlace de integridad'
                  : 'No archivado por backend'
            }
          >
            {evidenceManifestSupplementaryIntegrityRequired ? (
              <>
                <p>Manifiesto bloqueado por documentos suplementarios sin SHA-256, almacenamiento backend o referencia a la version actual.</p>
                <Link to={`/attachments/${documentId}`} className="underline">Gestionar adjuntos</Link>
              </>
            ) : (
              <Link to={`/export/${documentId}?source=api`} className="underline">Exportar evidencia</Link>
            )}
          </EvidenceCard>

          {hasEvidenceManifestAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de manifiesto de evidencias"
              available={manifestArchived}
              status="Resumen administrativo de manifiesto sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Archivo backend</dt>
                  <dd>{countLabel(manifestArchived ? 1 : 0, 'manifiesto archivado para revisión', 'manifiestos archivados para revisión')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(evidenceManifestVersionReferenceCount, 'referencia versionada de manifiesto', 'referencias versionadas de manifiesto')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de manifiesto</dt>
                  <dd>{countLabel(evidenceManifestAuditSignals.length, 'señal auditada de manifiesto', 'señales auditadas de manifiesto')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedEvidenceManifestAuditSignalCount, 'señal de manifiesto con referencia versionada', 'señales de manifiesto con referencia versionada')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparación administrativa sobre baseline técnico parcial de manifiesto; no sustituye XML UNECE completo, WORM unificado ni revisión legal.</p>
              <p className="mt-1 text-xs font-medium">No expone XML, storage buckets/keys, URLs, tokens, object-version IDs, digests, tipos de evento ni IDs de auditoría crudos.</p>
              <Link to={`/export/${documentId}?source=api`} className="mt-2 inline-block underline">Abrir exportación autorizada de manifiesto</Link>
            </EvidenceCard>
          )}

          {evidenceManifestAuditSignals.length > 0 && (
            <EvidenceCard
              title="Historial auditado de manifiesto de evidencias"
              available={evidenceManifestAuditSignals.length > 0}
              status="Historial de manifiesto sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales históricas</dt>
                  <dd>{countLabel(evidenceManifestAuditSignals.length, 'señal histórica de manifiesto', 'señales históricas de manifiesto')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedEvidenceManifestAuditSignalCount, 'señal de manifiesto con referencia versionada', 'señales de manifiesto con referencia versionada')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación pendiente</dt>
                  <dd>{countLabel(unversionedEvidenceManifestAuditSignalCount, 'señal de manifiesto pendiente de reconciliación versionada', 'señales de manifiesto pendientes de reconciliación versionada')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Resumen histórico agregado de manifiesto para revisión autorizada; no sustituye evidencia append-only/WORM ni XML UNECE completo.</p>
              <p className="mt-1 text-xs font-medium">No expone XML, storage buckets/keys, URLs, tokens, object-version IDs, digests, tipos de evento, IDs de auditoría ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir historial autorizado de manifiesto</Link>
            </EvidenceCard>
          )}

          {hasEvidenceManifestAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de traspaso de manifiesto de evidencias"
              available={manifestArchived && evidenceManifestAuditSignals.length > 0}
              status="Traspaso de manifiesto sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Archivo backend</dt>
                  <dd>{countLabel(manifestArchived ? 1 : 0, 'manifiesto listo para traspaso', 'manifiestos listos para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(evidenceManifestVersionReferenceCount, 'referencia versionada preparada para traspaso de manifiesto', 'referencias versionadas preparadas para traspaso de manifiesto')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(evidenceManifestAuditSignals.length, 'señal auditada disponible para traspaso de manifiesto', 'señales auditadas disponibles para traspaso de manifiesto')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Checklist operativo de manifiesto; baseline técnico parcial, no certificación legal ni prueba independiente de transferencia.</p>
              <p className="mt-1 text-xs font-medium">Muestra solo agregados: no expone XML, storage, URLs, tokens, object-version IDs, digests, eventos crudos ni payloads.</p>
              <Link to={`/export/${documentId}?source=api`} className="mt-2 inline-block underline">Abrir traspaso autorizado de manifiesto</Link>
            </EvidenceCard>
          )}

          <EvidenceCard
            title="Aceptación de procedimientos"
            available={procedureAcceptanceCount > 0}
            status={countLabel(procedureAcceptanceCount, 'aceptación registrada', 'aceptaciones registradas')}
          />

          <EvidenceCard
            title="Gobernanza de procedimientos acordados"
            available={procedureAcceptanceCount > 0}
            status={procedureAcceptanceCount > 0 ? countLabel(procedureAcceptanceCount, 'aceptación sanitizada', 'aceptaciones sanitizadas') : 'Sin aceptaciones backend'}
          >
            {procedureSetReferenceCount > 0 && <p>{countLabel(procedureSetReferenceCount, 'referencia de conjunto activo disponible', 'referencias de conjuntos activos disponibles')}</p>}
            {procedureSetVersions.length > 0 && <p>versión {procedureSetVersions.join(', ')}</p>}
            {procedureRoles.length > 0 && <p>roles cubiertos: {procedureRoles.join(', ')}</p>}
            <Link to={`/workflow/${documentId}`} className="underline">Revisar procedimientos activos</Link>
          </EvidenceCard>

          {procedureAcceptanceCount > 0 && (
            <EvidenceCard
              title="Preparación administrativa de procedimientos"
              available={procedureAcceptanceCount > 0}
              status="Resumen administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Aceptaciones</dt>
                  <dd>{countLabel(procedureAcceptanceCount, 'aceptación revisable', 'aceptaciones revisables')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Conjuntos</dt>
                  <dd>{countLabel(procedureSetReferenceCount, 'referencia de conjunto activo disponible', 'referencias de conjuntos activos disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Cobertura de roles</dt>
                  <dd>{countLabel(procedureRoles.length, 'rol cubierto', 'roles cubiertos')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Versión backend</dt>
                  <dd>{countLabel(procedureBackendVersionReferenceCount, 'referencia de versión backend disponible', 'referencias de versión backend disponibles')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Siguiente paso: mantener administración de procedimiento en el flujo autorizado y revisar auditoría backend si cambia el set</p>
              <p className="mt-1 text-xs font-medium">No expone digests de procedimiento, actores, IDs de versión ni detalles crudos de aceptación.</p>
              <Link to={`/workflow/${documentId}`} className="mt-2 inline-block underline">Abrir administración de procedimientos</Link>
            </EvidenceCard>
          )}

          {procedureAcceptanceCount > 0 && (
            <EvidenceCard
              title="Control de cambios de procedimientos"
              available={procedureAcceptanceCount > 0}
              status="Resumen de control sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Aceptaciones versionadas</dt>
                  <dd>{countLabel(versionedProcedureAcceptanceCount, 'aceptación versionada', 'aceptaciones versionadas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Conjuntos activos</dt>
                  <dd>{countLabel(procedureSetReferenceCount, 'referencia de conjunto activo bajo revisión', 'referencias de conjuntos activos bajo revisión')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría de procedimiento</dt>
                  <dd>{countLabel(procedureAuditSignalCount, 'señal auditada de procedimiento', 'señales auditadas de procedimiento')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación backend</dt>
                  <dd>{countLabel(procedureReconciliationReferenceCount, 'referencia backend disponible para reconciliación', 'referencias backend disponibles para reconciliación')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Usar flujo autorizado y auditoría backend para cambios de procedimiento; baseline técnico parcial.</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs de auditoría, digests, actores, IDs de versión, notas, storage keys ni detalles crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada de procedimientos</Link>
            </EvidenceCard>
          )}

          {procedureHandoffReady && (
            <EvidenceCard
              title="Checklist de traspaso de procedimientos"
              available={procedureHandoffReady}
              status="Traspaso administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Cobertura aceptada</dt>
                  <dd>{countLabel(procedureRoles.length, 'rol con aceptación registrada', 'roles con aceptación registrada')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(procedureAuditSignalCount, 'señal auditada disponible', 'señales auditadas disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(versionedProcedureAcceptanceCount, 'aceptación lista para traspaso reconciliado', 'aceptaciones listas para traspaso reconciliado')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Conjunto activo</dt>
                  <dd>{countLabel(procedureSetReferenceCount, 'referencia de conjunto activo para traspaso', 'referencias de conjuntos activos para traspaso')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparar traspaso administrativo con procedimientos aceptados, auditoría autorizada y reconciliación backend</p>
              <p className="mt-1 text-xs font-medium">No expone eventos de auditoría, IDs, digests, actores, versiones, notas, storage keys, tokens ni comentarios crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada para traspaso</Link>
            </EvidenceCard>
          )}

          {procedureAuditSignalCount > 0 && (
            <EvidenceCard
              title="Historial sanitizado de procedimientos"
              available={procedureAuditSignalCount > 0}
              status="Historial agregado de procedimientos"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales registradas</dt>
                  <dd>{countLabel(procedureAuditSignalCount, 'señal de procedimiento registrada', 'señales de procedimiento registradas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedProcedureAuditSignalCount, 'señal versionada de procedimiento', 'señales versionadas de procedimiento')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales sin versión</dt>
                  <dd>{countLabel(unversionedProcedureAuditSignalCount, 'señal sin versión de procedimiento', 'señales sin versión de procedimiento')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Mantener trazabilidad de cambios de procedimiento en auditoría autorizada; este resumen no es asesoría legal.</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs, roles, actores, versiones, notas, storage keys, tokens, digests ni detalles crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir historial autorizado de procedimientos</Link>
            </EvidenceCard>
          )}

          {procedureAdministrationPackageReady && (
            <EvidenceCard
              title="Paquete de administración de procedimientos"
              available={procedureAdministrationPackageReady}
              status="Paquete administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Aceptaciones</dt>
                  <dd>{countLabel(procedureAcceptanceCount, 'aceptación lista para administración', 'aceptaciones listas para administración')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Conjuntos</dt>
                  <dd>{countLabel(procedureSetReferenceCount, 'referencia de conjunto activo disponible para administración', 'referencias de conjuntos activos disponibles para administración')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría</dt>
                  <dd>{countLabel(procedureAuditSignalCount, 'señal administrativa auditada', 'señales administrativas auditadas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(versionedProcedureAuditSignalCount, 'señal versionada disponible para reconciliación', 'señales versionadas disponibles para reconciliación')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Administrar procedimiento desde flujo autorizado; usar auditoría backend para el expediente completo</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs de auditoría, IDs de aceptación, actores, versiones backend, digests, notas, XML, storage keys, tokens ni payloads.</p>
              <Link to={`/workflow/${documentId}`} className="mt-2 inline-block underline">Abrir flujo autorizado de procedimientos</Link>
            </EvidenceCard>
          )}

          {hasLifecycleAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de ciclo de vida"
              available={hasLifecycleAdministrationEvidence}
              status="Resumen de transiciones sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Estado backend</dt>
                  <dd>estado actual: {state.document?.status ?? 'desconocido'}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales de transición</dt>
                  <dd>{countLabel(lifecycleAuditSignals.length, 'señal auditada de ciclo de vida', 'señales auditadas de ciclo de vida')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Referencia backend</dt>
                  <dd>{countLabel(lifecycleVersionReferenceCount, 'señal con referencia de versión backend', 'señales con referencia de versión backend')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Revisión autorizada</dt>
                  <dd>auditoría backend disponible</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Usar flujo autorizado y auditoría backend para revisar transiciones; baseline técnico parcial</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs de auditoría, roles, IDs de versión, notas, storage keys, digests, XML, tokens ni detalles crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada de ciclo de vida</Link>
            </EvidenceCard>
          )}

          {hasLifecycleAdministrationEvidence && (
            <EvidenceCard
              title="Administración de transición de ciclo de vida"
              available={hasLifecycleAdministrationEvidence}
              status="Controles owner sobre endpoint backend"
            >
              <form onSubmit={handleLifecycleTransitionUpdate} className="space-y-2">
                <label className="block">
                  <span className="font-medium">Nuevo estado de ciclo de vida</span>
                  <select
                    className="mt-1 block w-full rounded border p-2 text-gray-900"
                    value={lifecycleNextStatus}
                    onChange={(event) => setLifecycleNextStatus(event.target.value as DocumentLifecycleStatus)}
                  >
                    <option value="issued">issued</option>
                    <option value="accepted_by_carrier">accepted_by_carrier</option>
                    <option value="in_transit">in_transit</option>
                    <option value="delivered">delivered</option>
                    <option value="closed">closed</option>
                    <option value="amended">amended</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </label>
                <label className="block">
                  <span className="font-medium">Motivo administrativo</span>
                  <input
                    className="mt-1 block w-full rounded border p-2 text-gray-900"
                    value={lifecycleReason}
                    onChange={(event) => setLifecycleReason(event.target.value)}
                    placeholder="Motivo sanitizado para auditoría backend autorizada"
                  />
                </label>
                <button
                  type="submit"
                  className="rounded bg-indigo-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                  disabled={lifecycleUpdating || lifecycleReloadRequired}
                >
                  {lifecycleUpdating ? 'Actualizando ciclo de vida...' : 'Actualizar ciclo de vida'}
                </button>
                {lifecycleUpdateMessage && <p className="text-xs font-medium">{lifecycleUpdateMessage}</p>}
                <p className="text-xs font-medium">Envía solo nuevo estado y motivo; no muestra detalles crudos, storage, digests, XML ni payloads de auditoría.</p>
              </form>
            </EvidenceCard>
          )}

          {hasLifecycleAdministrationEvidence && (
            <EvidenceCard
              title="Resultado de transiciones de ciclo de vida"
              available={hasLifecycleAdministrationEvidence}
              status="Resultado agregado sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-3">
                <div>
                  <dt className="font-medium">Aceptadas</dt>
                  <dd>{countLabel(lifecycleAcceptedTransitionCount, 'transición aceptada por backend', 'transiciones aceptadas por backend')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Rechazadas</dt>
                  <dd>{countLabel(lifecycleAuthorizedReviewCount, 'transición rechazada por backend', 'transiciones rechazadas por backend')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(lifecyclePendingVersionReconciliationCount, 'señal pendiente de reconciliación de versión', 'señales pendientes de reconciliación de versión')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Resultado agregado para administración owner; consultar auditoría autorizada para expediente completo</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs, roles, versiones, motivos, storage keys, digests, XML, tokens ni payloads.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada de resultados de ciclo de vida</Link>
            </EvidenceCard>
          )}

          {hasLifecycleAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de traspaso de ciclo de vida"
              available={hasLifecycleAdministrationEvidence}
              status="Traspaso administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Estado backend</dt>
                  <dd>estado actual listo para traspaso: {state.document?.status ?? 'desconocido'}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales auditadas</dt>
                  <dd>{countLabel(lifecycleAuditSignals.length, 'señal auditada disponible para traspaso', 'señales auditadas disponibles para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(lifecycleVersionReferenceCount, 'señal versionada lista para reconciliación', 'señales versionadas listas para reconciliación')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Revisión autorizada</dt>
                  <dd>{countLabel(lifecycleAuthorizedReviewCount, 'revisión autorizada disponible', 'revisiones autorizadas disponibles')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparar traspaso administrativo con estado backend, señales auditadas y revisión autorizada</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs de auditoría, roles, IDs de versión, notas, storage keys, digests, XML, tokens ni detalles crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir auditoría autorizada para traspaso de ciclo de vida</Link>
            </EvidenceCard>
          )}

          {retentionAuditSignalCount > 0 && (
            <EvidenceCard
              title="Historial sanitizado de retención y legal hold"
              available={retentionAuditSignalCount > 0}
              status="Historial agregado de retención"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Señales registradas</dt>
                  <dd>{countLabel(retentionAuditSignalCount, 'señal de gobernanza de retención registrada', 'señales de gobernanza de retención registradas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales versionadas</dt>
                  <dd>{countLabel(versionedRetentionAuditSignalCount, 'señal versionada de retención', 'señales versionadas de retención')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Señales sin versión</dt>
                  <dd>{countLabel(unversionedRetentionAuditSignalCount, 'señal sin versión de retención', 'señales sin versión de retención')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Mantener trazabilidad de retención y legal hold en auditoría autorizada; baseline técnico parcial.</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs, roles, actores, IDs de versión, razones, history notes, storage keys, tokens, digests, XML ni detalles crudos.</p>
              <Link to={`/audit/${documentId}`} className="mt-2 inline-block underline">Abrir historial autorizado de retención</Link>
            </EvidenceCard>
          )}

          {hasRetentionAdministrationEvidence && (
            <EvidenceCard
              title="Preparación administrativa de retención y legal hold"
              available={hasRetentionAdministrationEvidence}
              status="Resumen administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Política vigente</dt>
                  <dd>{countLabel(retentionPolicyCount, 'política de retención activa', 'políticas de retención activas')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Legal hold</dt>
                  <dd>{countLabel(activeLegalHoldCount, 'legal hold activo', 'legal holds activos')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(retentionAuditSignalCount, 'señal auditada de retención disponible', 'señales auditadas de retención disponibles')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(versionedRetentionAuditSignalCount, 'señal versionada lista para reconciliación', 'señales versionadas listas para reconciliación')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparar revisión administrativa con política vigente, legal hold y auditoría autorizada</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs, roles, actores, IDs de versión, razones, history notes, storage keys, tokens, digests, XML ni detalles crudos.</p>
              <Link to={`/retention/${documentId}`} className="mt-2 inline-block underline">Abrir administración de retención autorizada</Link>
            </EvidenceCard>
          )}

          {hasRetentionAdministrationEvidence && (
            <EvidenceCard
              title="Checklist de traspaso de retención y legal hold"
              available={hasRetentionAdministrationEvidence}
              status="Traspaso administrativo sanitizado"
            >
              <dl className="grid gap-2 sm:grid-cols-2">
                <div>
                  <dt className="font-medium">Política</dt>
                  <dd>{countLabel(retentionPolicyCount, 'política lista para traspaso', 'políticas listas para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Legal hold</dt>
                  <dd>{countLabel(activeLegalHoldCount, 'legal hold preparado para traspaso', 'legal holds preparados para traspaso')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Auditoría autorizada</dt>
                  <dd>{countLabel(retentionAuditSignalCount, 'señal auditada disponible para traspaso de retención', 'señales auditadas disponibles para traspaso de retención')}</dd>
                </div>
                <div>
                  <dt className="font-medium">Reconciliación</dt>
                  <dd>{countLabel(versionedRetentionAuditSignalCount, 'referencia versionada preparada para traspaso de retención', 'referencias versionadas preparadas para traspaso de retención')}</dd>
                </div>
              </dl>
              <p className="mt-2 text-xs font-medium">Preparar traspaso administrativo con política vigente, legal hold, auditoría autorizada y reconciliación backend</p>
              <p className="mt-1 text-xs font-medium">No expone tipos de evento, IDs, roles, actores, IDs de versión, razones, history notes, storage keys, tokens, digests, XML ni detalles crudos.</p>
              <Link to={`/retention/${documentId}`} className="mt-2 inline-block underline">Abrir retención autorizada para traspaso</Link>
            </EvidenceCard>
          )}

          <EvidenceCard
            title="Gobernanza de retención/legal hold"
            available={hasRetentionGovernance}
            status={hasRetentionGovernance ? 'Política activa o legal hold registrado' : 'Sin política activa ni legal hold'}
          >
            {retentionPolicy?.retentionMode && <p>modo: {retentionPolicy.retentionMode}</p>}
            {retentionPolicy?.retainUntil && <p>fecha de retención disponible</p>}
            {retentionPolicy?.jurisdiction && <p>jurisdicción: {retentionPolicy.jurisdiction}</p>}
            {legalHold?.active && <p>legal hold activo</p>}
            {hasBackendVersionReference(retentionPolicy) && <p>referencia de versión backend disponible</p>}
            <Link to={`/retention/${documentId}`} className="underline">Abrir retención y legal hold</Link>
          </EvidenceCard>
          </div>
        </>
      )}
    </div>
  );
};

export default EvidenceDashboard;
